import {t, useLanguage} from '../i18n';
import MarketPicker from "./MarketPicker";
import useMenuAdminAccess from "../hooks/useMenuAdminAccess";
// src/components/Header.jsx
// LOOP MOBILE HEADER SEAM GAP FIX HOTFIX
// Stable shared header for web + native.
// Uses public/logo.png as the default logo source to avoid broken asset URLs in mobile builds.

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import AvatarDropdown from './AvatarDropdown';
import MobileNavigation from './MobileNavigation';
import NotificationCenter from './NotificationCenter';
import SearchSuggestions from '../SearchSuggestions';
import { db as defaultDb, auth as defaultAuth } from '../firebase';
import { collection, collectionGroup, getDocs, query, where, limit } from 'firebase/firestore';
import { FaSearch, FaTimes } from 'react-icons/fa';
import useLocationSuggestions from '../hooks/useLocationSuggestions';
import importedLogo from '../assets/images/logo.png';
import importedLogoAlt from '../assets/images/logo3.png';
import { businessPublicPath } from '../utils/businessSlug';
import getUserBusinessesAndRoles, { getCachedUserBusinesses } from '../services/userBusinesses';
import { isIosCapacitor } from '../utils/nativeHttp';
import { getEffectiveManualAuthSession } from '../utils/manualAuth';
import fsClient from '../data/firestoreClient';
import '../styles/loopLayers.css';
import '../styles/headerPremium.css';


function isPublicBusiness(biz) {
  if (!biz || typeof biz !== "object") return false;
  return (
    biz.visible !== false &&
    biz.hidden !== true &&
    biz.isActive !== false &&
    biz.published !== false
  );
}

const IMPORTED_LOGO = importedLogo && (importedLogo.default || importedLogo) ? (importedLogo.default || importedLogo) : '';
const IMPORTED_LOGO_ALT = importedLogoAlt && (importedLogoAlt.default || importedLogoAlt) ? (importedLogoAlt.default || importedLogoAlt) : '';


const LOOP_BUSINESS_ACCESS_CACHE_VERSION = "v6-business-membership";
const LOOP_BUSINESS_ACCESS_CACHE_TTL_MS = 1000 * 60 * 60 * 24 * 14; // 14 días

function normalizeAccessEmail(value) {
  return String(value || "").trim().toLowerCase();
}

function businessAccessCacheKey(email) {
  const safe = normalizeAccessEmail(email);
  return safe ? `loop:businessAccess:${LOOP_BUSINESS_ACCESS_CACHE_VERSION}:${safe}` : "";
}

function readBusinessAccessCache(email) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    const key = businessAccessCacheKey(email);
    if (!key) return null;
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const savedAt = Number(parsed && parsed.savedAt);
    if (!savedAt || Date.now() - savedAt > LOOP_BUSINESS_ACCESS_CACHE_TTL_MS) return null;
    return {
      ownsBusiness: parsed.ownsBusiness === true,
      workerAccess: parsed.workerAccess === true,
      savedAt,
    };
  } catch (_) {
    return null;
  }
}

function writeBusinessAccessCache(accessCacheId, value = {}) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    const key = businessAccessCacheKey(accessCacheId);
    if (!key) return;
    window.localStorage.setItem(key, JSON.stringify({
      ownsBusiness: value.ownsBusiness === true,
      workerAccess: value.workerAccess === true,
      savedAt: Date.now(),
    }));
  } catch (_) {}
}

function clearBusinessAccessCache(email) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    const key = businessAccessCacheKey(email);
    if (key) window.localStorage.removeItem(key);
  } catch (_) {}
}

function headerWithTimeout(promise, timeoutMs = 4500, fallback = null) {
  let timer = null;
  return Promise.race([
    promise,
    new Promise((resolve) => {
      timer = setTimeout(() => resolve(fallback), timeoutMs);
    }),
  ]).finally(() => {
    try { if (timer) clearTimeout(timer); } catch (_) {}
  });
}

function getIosHeaderEffectiveUser(userEmail) {
  try {
    const session = getEffectiveManualAuthSession();
    if (session && session.loggedIn && session.user) {
      return {
        uid: session.user.uid || null,
        email: session.user.email || userEmail || "",
        displayName: session.user.displayName || "",
      };
    }
  } catch (_) {}
  return userEmail ? { uid: null, email: userEmail } : null;
}

function getHeaderEffectiveIdentity(userEmail, userName) {
  const email = normalizeAccessEmail(userEmail);
  let uid = null;
  let displayName = String(userName || "").trim();

  try {
    if (defaultAuth && defaultAuth.currentUser) {
      uid = defaultAuth.currentUser.uid || uid;
      if (!displayName) displayName = defaultAuth.currentUser.displayName || "";
    }
  } catch (_) {}

  try {
    const session = getEffectiveManualAuthSession();
    if (session && session.loggedIn && session.user) {
      uid = session.user.uid || uid;
      if (!displayName) displayName = session.user.displayName || "";
      return { uid: uid || null, email: normalizeAccessEmail(session.user.email || email), displayName };
    }
  } catch (_) {}

  return { uid: uid || null, email, displayName };
}

function businessAccessCacheIdentity(identity = {}) {
  const email = normalizeAccessEmail(identity.email);
  if (email) return email;
  const uid = String(identity.uid || "").trim();
  return uid ? `uid:${uid}` : "";
}

function summarizeRoleAccess(list) {
  const arr = Array.isArray(list) ? list : [];
  return {
    ownsBusiness: arr.some((item) => item && item.role === "owner"),
    workerAccess: arr.some((item) => item && item.role === "worker"),
  };
}


function headerBusinessRowCanBeManaged(row) {
  if (!row || typeof row !== "object") return false;
  return true; // Membership query and server rules determine access, not public visibility.
}

async function headerNativeListBusinessesWhere(field, op, value, max = 3, db) {
  const cleanField = String(field || "").trim();
  const cleanValue = String(value || "").trim();
  if (!cleanField || !cleanValue || !fsClient || typeof fsClient.listCollectionByPath !== "function") return [];
  try {
    const rows = await headerWithTimeout(
      fsClient.listCollectionByPath(
        "businesses",
        { where: [{ field: cleanField, op, value: cleanValue }], limit: max },
        { db }
      ),
      3600,
      []
    );
    return Array.isArray(rows) ? rows.filter(headerBusinessRowCanBeManaged) : [];
  } catch (err) {
    try {
      if (window.__LOOP_HEADER_ACCESS_DEBUG__) {
        console.warn("[Header] native business access query failed", { field: cleanField, op, message: err?.message || err });
      }
    } catch (_) {}
    return [];
  }
}

async function resolveNativeHeaderBusinessAccess(db, identity = {}) {
  const email = normalizeAccessEmail(identity.email);
  const uid = String(identity.uid || "").trim();
  const checks = [];

  if (email) {
    checks.push(headerNativeListBusinessesWhere("ownerEmail", "EQUAL", email, 1, db).then((rows) => ({ type: "owner", rows })));
    checks.push(headerNativeListBusinessesWhere("ownerEmailLower", "EQUAL", email, 1, db).then((rows) => ({ type: "owner", rows })));
    checks.push(headerNativeListBusinessesWhere("requestedByEmail", "EQUAL", email, 1, db).then((rows) => ({ type: "owner", rows })));
    checks.push(headerNativeListBusinessesWhere("workerAccessEmails", "ARRAY_CONTAINS", email, 1, db).then((rows) => ({ type: "worker", rows })));
    checks.push(headerNativeListBusinessesWhere("workerEmails", "ARRAY_CONTAINS", email, 1, db).then((rows) => ({ type: "worker", rows })));
    checks.push(headerNativeListBusinessesWhere("adminEmails", "ARRAY_CONTAINS", email, 1, db).then((rows) => ({ type: "owner", rows })));
  }

  if (uid) {
    checks.push(headerNativeListBusinessesWhere("ownerUid", "EQUAL", uid, 1, db).then((rows) => ({ type: "owner", rows })));
    checks.push(headerNativeListBusinessesWhere("userId", "EQUAL", uid, 1, db).then((rows) => ({ type: "owner", rows })));
    checks.push(headerNativeListBusinessesWhere("workerAccessUids", "ARRAY_CONTAINS", uid, 1, db).then((rows) => ({ type: "worker", rows })));
    checks.push(headerNativeListBusinessesWhere("workerUids", "ARRAY_CONTAINS", uid, 1, db).then((rows) => ({ type: "worker", rows })));
    checks.push(headerNativeListBusinessesWhere("admins", "ARRAY_CONTAINS", uid, 1, db).then((rows) => ({ type: "owner", rows })));
  }

  if (!checks.length) return { ownsBusiness: false, workerAccess: false };

  const results = await Promise.allSettled(checks);
  let ownsBusiness = false;
  let workerAccess = false;
  results.forEach((result) => {
    if (result.status !== "fulfilled") return;
    const payload = result.value || {};
    const found = Array.isArray(payload.rows) && payload.rows.length > 0;
    if (!found) return;
    if (payload.type === "owner") ownsBusiness = true;
    if (payload.type === "worker") workerAccess = true;
  });

  return { ownsBusiness, workerAccess };
}

// Single clean logo system for Web, Vercel, Android WebView and iOS WKWebView.
// Use bundled imports only. Avoid PUBLIC_URL and absolute /logo.png paths because
// they can fail on deployed SPA routes even when they work on localhost.
const PRIMARY_COLOR = '#00675f';
const PRIMARY_COLOR_2 = '#00897f';
const PRIMARY_DARK = '#064e47';
const SURFACE_WHITE = 'rgba(255,255,255,0.96)';
const HEADER_SHADOW = '0 14px 34px rgba(6, 78, 71, 0.22)';
const CONTROL_SHADOW = '0 10px 24px rgba(15, 23, 42, 0.10)';
const LOOP_HEADER_LOCK_BG_COLOR = '#134f47';
const LOOP_HEADER_LOCK_BG_IMAGE = 'radial-gradient(circle at 10% 0%, rgba(255,248,238,.12), transparent 28%), linear-gradient(135deg, #103f39 0%, #14554d 56%, #1d6b61 100%)';
const LOOP_HEADER_LOCK_BG = `${LOOP_HEADER_LOCK_BG_IMAGE}`;


// ----- Native header sizing (tweak here) -----
const NATIVE_HEADER_PAD_X = 12;
const NATIVE_TOP_GAP = 0;
const NATIVE_LOGO_ROW_H = 42;
const NATIVE_ACTION_ROW_H = 44;
const NATIVE_TOTAL_H = NATIVE_TOP_GAP + NATIVE_LOGO_ROW_H + NATIVE_ACTION_ROW_H;
const NATIVE_CONTROL_H = 40;
// --------------------------------------------

// WEB-ONLY: horizontal logo shift (px).
const WEB_LOGO_SHIFT_RIGHT_PX = 0;

const headerAirbnbStyles = `
  .loop-topbar-shell { width: min(100%, 1440px); margin: 0 auto; padding: 10px clamp(10px, 1.6vw, 18px); }
  .loop-topbar { display: grid; grid-template-columns: 160px minmax(360px, 1fr) auto; align-items: center; gap: 16px; min-height: 62px; }
  .loop-logo-btn { transition: transform 160ms ease, opacity 160ms ease; }
  .loop-logo-btn:active { transform: scale(0.985); }
  .loop-search-wrap { display: grid; grid-template-columns: minmax(260px, 1.12fr) minmax(220px, 0.88fr); gap: 12px; align-items: center; }
  .loop-input-card { position: relative; }
  .loop-header-input { width: 100%; height: 52px; border-radius: 13px; border: 1px solid rgba(226,232,240,0.72); background: rgba(255,255,255,0.96); box-shadow: 0 10px 24px rgba(15,23,42,0.10); outline: none; padding: 0 46px 0 48px; color: #0f172a; font-size: 14px; font-weight: 700; transition: box-shadow 160ms ease, border-color 160ms ease; }
  .loop-header-input:focus { border-color: rgba(20,184,166,0.40); box-shadow: 0 0 0 4px rgba(20,184,166,0.12), 0 12px 28px rgba(15,23,42,0.12); }
  .loop-input-icon { position: absolute; left: 18px; top: 50%; transform: translateY(-50%); color: #64748b; z-index: 2; }
  .loop-action-nav { display: flex; align-items: center; justify-content: flex-end; gap: 14px; color: #ffffff; }
  .loop-nav-btn { display: inline-flex; align-items: center; gap: 8px; border: 0; background: transparent; color: #ffffff; font-weight: 850; font-size: 14px; white-space: nowrap; cursor: pointer; padding: 8px 4px; border-radius: 999px; transition: background 160ms ease, transform 160ms ease; }
  .loop-nav-btn:hover { background: rgba(255,255,255,0.10); }
  .loop-nav-btn:active { transform: scale(0.98); }
  .loop-bell-btn, .loop-avatar-pro { width: 46px; height: 46px; border-radius: 999px; border: 1px solid rgba(255,255,255,0.35); background: rgba(255,255,255,0.96); color: #00675f; display: inline-flex; align-items: center; justify-content: center; font-weight: 950; box-shadow: 0 12px 28px rgba(15,23,42,0.16); cursor: pointer; position: relative; }
  .loop-avatar-pro { gap: 6px; }
  .loop-badge-pro { position: absolute; right: -3px; top: -4px; min-width: 19px; height: 19px; border-radius: 999px; background: #ffffff; color: #00675f; border: 2px solid #00675f; display: inline-flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 950; }
  .loop-native-search-overlay { backdrop-filter: blur(14px); -webkit-backdrop-filter: blur(14px); }
  @media (max-width: 980px) { .loop-topbar { grid-template-columns: 128px minmax(280px, 1fr) auto; gap: 10px; } .loop-search-wrap { grid-template-columns: 1fr; } .loop-location-web { display: none; } }
`;



const headerAirbnbRefStyles = `
  .loop-ref-header {
    position: sticky !important;
    top: 0 !important;
    z-index: var(--loop-z-header, 1000) !important;
    width: 100% !important;
    min-height: 78px !important;
    background-color: #00675f !important;
    background-image: linear-gradient(135deg, #005c55 0%, #006f66 50%, #00534d 100%) !important;
    border-bottom: 0 !important;
    box-shadow: 0 16px 36px rgba(0, 76, 70, 0.28) !important;
    padding-top: env(safe-area-inset-top) !important;
    overflow: visible !important;
  }

  .loop-ref-header-inner {
    width: min(100%, 1540px) !important;
    margin: 0 auto !important;
    padding: 12px clamp(20px, 3vw, 46px) !important;
    display: grid !important;
    grid-template-columns: 180px minmax(520px, 1fr) auto !important;
    align-items: center !important;
    gap: clamp(18px, 2.4vw, 32px) !important;
    min-height: 78px !important;
    box-sizing: border-box !important;
    background: transparent !important;
  }

  .loop-ref-logo-button {
    background: transparent !important;
    border: 0 !important;
    padding: 0 !important;
    cursor: pointer !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: flex-start !important;
    min-width: 0 !important;
  }

  .loop-ref-search-wrap {
    min-width: 0 !important;
    display: grid !important;
    grid-template-columns: minmax(320px, 720px) !important;
    gap: 14px !important;
    align-items: center !important;
    background: transparent !important;
  }

  .loop-ref-search-wrap form,
  .loop-ref-search-wrap > div {
    min-width: 0 !important;
    background: transparent !important;
  }

  .loop-ref-search-input {
    width: 100% !important;
    height: 56px !important;
    border-radius: 12px !important;
    border: 1px solid rgba(226,232,240,0.92) !important;
    background: #ffffff !important;
    box-shadow: 0 12px 28px rgba(15,23,42,0.16) !important;
    outline: none !important;
    padding: 0 46px 0 50px !important;
    color: #0f172a !important;
    font-size: 14px !important;
    font-weight: 750 !important;
    letter-spacing: -0.01em !important;
    box-sizing: border-box !important;
  }

  .loop-ref-search-input::placeholder {
    color: #64748b !important;
    opacity: 0.86 !important;
    font-weight: 650 !important;
  }

  .loop-ref-search-input:focus {
    border-color: rgba(20,184,166,0.58) !important;
    box-shadow: 0 0 0 4px rgba(20,184,166,0.15), 0 16px 34px rgba(15,23,42,0.18) !important;
  }

  .loop-ref-icon-left {
    position: absolute !important;
    left: 18px !important;
    top: 50% !important;
    transform: translateY(-50%) !important;
    color: #475569 !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    z-index: 2 !important;
    pointer-events: none !important;
  }

  .loop-ref-actions {
    min-width: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: flex-end !important;
    gap: 16px !important;
    color: #ffffff !important;
    background: transparent !important;
  }

  .loop-ref-nav-btn {
    height: 44px !important;
    border-radius: 999px !important;
    border: 1px solid transparent !important;
    background: transparent !important;
    color: #ffffff !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 9px !important;
    padding: 0 8px !important;
    font-weight: 850 !important;
    font-size: 15px !important;
    letter-spacing: -0.01em !important;
    white-space: nowrap !important;
    cursor: pointer !important;
    position: relative !important;
  }

  .loop-ref-nav-btn:hover {
    background: rgba(255,255,255,0.11) !important;
    border-color: rgba(255,255,255,0.18) !important;
  }

  .loop-ref-nav-btn svg {
    width: 22px !important;
    height: 22px !important;
    stroke-width: 2.15 !important;
    color: #ffffff !important;
  }

  .loop-ref-bell,
  .loop-ref-avatar {
    width: 46px !important;
    height: 46px !important;
    border-radius: 999px !important;
    border: 1px solid rgba(255,255,255,0.38) !important;
    background: #ffffff !important;
    color: #00675f !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    box-shadow: 0 12px 28px rgba(15,23,42,0.17) !important;
    cursor: pointer !important;
    position: relative !important;
    flex: 0 0 auto !important;
  }

  .loop-ref-bell svg {
    width: 22px !important;
    height: 22px !important;
    stroke-width: 2.15 !important;
    color: #00675f !important;
  }

  .loop-ref-avatar {
    font-weight: 900 !important;
    font-size: 15px !important;
  }

  .loop-ref-badge {
    position: absolute !important;
    right: -4px !important;
    top: -5px !important;
    min-width: 20px !important;
    height: 20px !important;
    padding: 0 6px !important;
    border-radius: 999px !important;
    background: #ffffff !important;
    color: #00675f !important;
    border: 2px solid #00675f !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    font-size: 11px !important;
    font-weight: 950 !important;
    line-height: 20px !important;
    box-sizing: border-box !important;
    box-shadow: 0 6px 14px rgba(15,23,42,0.16) !important;
  }

  @media (max-width: 1220px) {
    .loop-ref-header-inner {
      grid-template-columns: 150px minmax(340px, 1fr) auto !important;
      gap: 14px !important;
      padding-left: 18px !important;
      padding-right: 18px !important;
    }
    .loop-ref-search-wrap {
      grid-template-columns: minmax(240px, 1fr) !important;
    }
    .loop-ref-search-wrap > div {
      display: none !important;
    }
    .loop-ref-actions {
      gap: 10px !important;
    }
    .loop-ref-nav-btn {
      padding: 0 6px !important;
      font-size: 14px !important;
    }
  }

  @media (max-width: 900px) {
    .loop-ref-header {
      background-color: #00675f !important;
      background-image: linear-gradient(135deg, #005c55 0%, #006f66 55%, #00534d 100%) !important;
    }
    .loop-ref-header-inner {
      grid-template-columns: auto 1fr !important;
      grid-template-areas:
        "logo actions"
        "search search" !important;
      gap: 10px 12px !important;
      min-height: auto !important;
      padding: 10px 14px 12px !important;
    }
    .loop-ref-logo-button {
      grid-area: logo !important;
    }
    .loop-ref-search-wrap {
      grid-area: search !important;
      grid-template-columns: 1fr !important;
      gap: 10px !important;
    }
    .loop-ref-actions {
      grid-area: actions !important;
      justify-content: flex-end !important;
      gap: 8px !important;
    }
    .loop-ref-search-input {
      height: 50px !important;
      border-radius: 14px !important;
      padding-left: 46px !important;
      font-size: 14px !important;
    }
    .loop-ref-nav-btn {
      width: 42px !important;
      height: 42px !important;
      padding: 0 !important;
      border-radius: 999px !important;
      background: rgba(255,255,255,0.12) !important;
      border-color: rgba(255,255,255,0.18) !important;
    }
    .loop-ref-nav-btn span {
      display: none !important;
    }
    .loop-ref-bell,
    .loop-ref-avatar {
      width: 42px !important;
      height: 42px !important;
    }
  }

  @media (max-width: 520px) {
    .loop-ref-header-inner {
      padding-left: 12px !important;
      padding-right: 12px !important;
    }
    .loop-ref-actions {
      gap: 7px !important;
    }
    .loop-ref-nav-btn {
      width: 40px !important;
      height: 40px !important;
    }
    .loop-ref-bell,
    .loop-ref-avatar {
      width: 40px !important;
      height: 40px !important;
    }
    .loop-ref-search-input {
      height: 48px !important;
    }
  }

  /* =========================================================
     HARD FIX: SMALL WEB HEADER / TABLET / MOBILE BROWSER
     The header must stop acting like desktop when viewport is narrow.
     ========================================================= */

  @media (max-width: 920px) {
    .loop-ref-header {
      position: sticky !important;
      top: 0 !important;
      min-height: 126px !important;
      height: auto !important;
      overflow: visible !important;
      padding-top: env(safe-area-inset-top, 0px) !important;
    }

    .loop-ref-header-inner {
      width: 100% !important;
      min-height: 126px !important;
      height: auto !important;
      padding: 10px 12px 12px !important;
      box-sizing: border-box !important;

      display: grid !important;
      grid-template-columns: minmax(88px, auto) 1fr auto !important;
      grid-template-rows: 44px 54px !important;
      grid-template-areas:
        "logo spacer actions"
        "search search search" !important;

      align-items: center !important;
      gap: 10px !important;
      overflow: visible !important;
    }

    .loop-ref-logo-button {
      grid-area: logo !important;
      height: 44px !important;
      max-height: 44px !important;
      align-self: center !important;
      justify-self: start !important;
      overflow: hidden !important;
    }

    .loop-ref-logo-button > div {
      height: 44px !important;
      max-height: 44px !important;
      max-width: 116px !important;
      overflow: hidden !important;
    }

    .loop-ref-logo-button img {
      max-height: 40px !important;
      max-width: 116px !important;
      object-fit: contain !important;
    }

    .loop-ref-actions {
      grid-area: actions !important;
      height: 44px !important;
      max-height: 44px !important;
      align-self: center !important;
      justify-self: end !important;
      display: flex !important;
      align-items: center !important;
      justify-content: flex-end !important;
      gap: 7px !important;
      overflow: visible !important;
      transform: none !important;
      margin: 0 !important;
      padding: 0 !important;
    }

    .loop-ref-search-wrap {
      grid-area: search !important;
      width: 100% !important;
      min-width: 0 !important;
      align-self: center !important;
      display: block !important;
      margin: 0 !important;
      padding: 0 !important;
      overflow: visible !important;
    }

    .loop-ref-search-wrap form {
      width: 100% !important;
      min-width: 0 !important;
      height: 52px !important;
      margin: 0 !important;
      padding: 0 !important;
      position: relative !important;
      display: block !important;
    }

    .loop-ref-search-input {
      width: 100% !important;
      min-width: 0 !important;
      height: 52px !important;
      max-height: 52px !important;
      border-radius: 17px !important;
      padding: 0 44px 0 46px !important;
      box-sizing: border-box !important;
      display: block !important;
      margin: 0 !important;
    }

    .loop-ref-icon-left {
      left: 16px !important;
      top: 50% !important;
      transform: translateY(-50%) !important;
    }

    .loop-ref-nav-btn {
      width: 40px !important;
      height: 40px !important;
      max-height: 40px !important;
      min-height: 40px !important;
      padding: 0 !important;
      border-radius: 999px !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      flex: 0 0 40px !important;
      background: rgba(255,255,255,.12) !important;
      border-color: rgba(255,255,255,.18) !important;
      overflow: visible !important;
    }

    .loop-ref-nav-btn span:not(.loop-ref-badge) {
      display: none !important;
    }

    .loop-ref-bell,
    .loop-ref-avatar {
      width: 40px !important;
      height: 40px !important;
      max-height: 40px !important;
      min-height: 40px !important;
      flex: 0 0 40px !important;
      align-self: center !important;
      margin: 0 !important;
      transform: none !important;
    }
  }

  @media (max-width: 480px) {
    .loop-ref-header {
      min-height: 120px !important;
    }

    .loop-ref-header-inner {
      min-height: 120px !important;
      grid-template-rows: 42px 50px !important;
      padding: 8px 10px 10px !important;
      gap: 8px !important;
    }

    .loop-ref-search-wrap form,
    .loop-ref-search-input {
      height: 50px !important;
      max-height: 50px !important;
    }

    .loop-ref-actions {
      gap: 6px !important;
    }

    .loop-ref-nav-btn,
    .loop-ref-bell,
    .loop-ref-avatar {
      width: 38px !important;
      height: 38px !important;
      min-height: 38px !important;
      max-height: 38px !important;
      flex-basis: 38px !important;
    }
  }




  /* =========================================================
     LOOP HEADER V2 — thinner premium web navbar
     ========================================================= */
  @media (min-width: 921px) {
    .loop-ref-header {
      min-height: 66px !important;
      background-image: linear-gradient(135deg, #005e57 0%, #006f66 52%, #00534d 100%) !important;
      box-shadow: 0 12px 30px rgba(0, 76, 70, 0.22) !important;
    }

    .loop-ref-header-inner {
      min-height: 66px !important;
      padding-top: 8px !important;
      padding-bottom: 8px !important;
      grid-template-columns: 160px minmax(420px, 680px) auto !important;
    }

    .loop-ref-logo-button img {
      max-height: 42px !important;
    }

    .loop-ref-search-input {
      height: 48px !important;
      border-radius: 14px !important;
      box-shadow: 0 10px 22px rgba(15,23,42,0.13) !important;
    }

    .loop-ref-nav-btn {
      height: 40px !important;
      font-size: 14px !important;
    }

    .loop-ref-bell,
    .loop-ref-avatar {
      width: 42px !important;
      height: 42px !important;
    }
  }


  .loop-owner-console-btn {
    position: relative !important;
    isolation: isolate !important;
    padding: 0 13px !important;
    border-color: rgba(255,250,242,.28) !important;
    background:
      radial-gradient(circle at 18% 0%, rgba(255,255,255,.24), transparent 34%),
      linear-gradient(135deg, rgba(255,250,242,.20), rgba(255,250,242,.10)) !important;
    color: #fffaf2 !important;
    box-shadow: inset 0 1px 0 rgba(255,255,255,.16) !important;
  }

  .loop-owner-console-btn::before {
    content: "" !important;
    width: 7px !important;
    height: 7px !important;
    border-radius: 999px !important;
    background: #2dd4bf !important;
    box-shadow: 0 0 0 4px rgba(45,212,191,.15) !important;
    flex: 0 0 auto !important;
  }

  .loop-owner-console-btn:hover {
    background:
      radial-gradient(circle at 18% 0%, rgba(255,255,255,.30), transparent 34%),
      linear-gradient(135deg, rgba(255,250,242,.26), rgba(255,250,242,.14)) !important;
  }

  @media (max-width: 920px) {
    .loop-owner-console-btn {
      width: 40px !important;
      padding: 0 !important;
    }
    .loop-owner-console-btn::before {
      width: 8px !important;
      height: 8px !important;
    }
  }


  /* LOOP PHASE 3 HEADER — calm luxury floating glass */
  @media (min-width: 921px) {
    .loop-ref-header {
      min-height: 64px !important;
      background:
        linear-gradient(135deg, rgba(0,88,81,.92), rgba(0,103,95,.92)) !important;
      box-shadow: 0 18px 46px rgba(0, 76, 70, .20) !important;
      backdrop-filter: blur(18px) saturate(1.1) !important;
      -webkit-backdrop-filter: blur(18px) saturate(1.1) !important;
    }

    .loop-ref-header-inner {
      min-height: 64px !important;
      padding-top: 7px !important;
      padding-bottom: 7px !important;
      width: min(100%, 1580px) !important;
    }

    .loop-ref-search-input {
      height: 46px !important;
      border-radius: 15px !important;
      border-color: rgba(255,255,255,.78) !important;
      box-shadow: 0 12px 28px rgba(15,23,42,.13) !important;
    }

    .loop-ref-nav-btn {
      height: 38px !important;
      font-size: 13.5px !important;
    }

    .loop-ref-bell,
    .loop-ref-avatar {
      width: 42px !important;
      height: 42px !important;
      box-shadow: 0 12px 28px rgba(15,23,42,.14) !important;
    }
  }

  /* LOOP PHASE 3A HEADER — Soho House glass navigation */
  @media (min-width: 921px) {
    .loop-ref-header {
      min-height: 68px !important;
      background: linear-gradient(135deg, rgba(35,31,25,.82), rgba(0,103,95,.72)) !important;
      border-bottom: 0 !important;
      box-shadow: 0 18px 50px rgba(35,31,25,.18) !important;
      backdrop-filter: blur(22px) saturate(1.08) !important;
      -webkit-backdrop-filter: blur(22px) saturate(1.08) !important;
    }
    .loop-ref-header-inner {
      min-height: 68px !important;
      padding-top: 8px !important;
      padding-bottom: 8px !important;
      width: min(100%, 1660px) !important;
      grid-template-columns: 180px minmax(460px, 740px) auto !important;
    }
    .loop-ref-search-input {
      height: 48px !important;
      border-radius: 999px !important;
      border: 1px solid rgba(255,250,242,.72) !important;
      background: rgba(255,250,242,.94) !important;
      box-shadow: 0 14px 34px rgba(35,31,25,.14) !important;
      color: #14110d !important;
    }
    .loop-ref-search-input::placeholder { color: #7a6b5b !important; }
    .loop-ref-nav-btn {
      height: 38px !important;
      padding: 0 10px !important;
      color: #fffaf2 !important;
      font-size: 13.5px !important;
      font-weight: 820 !important;
    }
    .loop-ref-bell, .loop-ref-avatar {
      width: 42px !important;
      height: 42px !important;
      background: rgba(255,250,242,.96) !important;
      color: #00675f !important;
      box-shadow: 0 12px 30px rgba(35,31,25,.16) !important;
    }
  }

  /* LOOP PHASE 3B DIRECT HEADER FIX — subtle tonal green */
  @media (min-width: 921px) {
    .loop-ref-header {
      min-height: 64px !important;
      background-color: #00675f !important;
      background-image: linear-gradient(135deg, #006058 0%, #006a61 100%) !important;
      box-shadow: 0 10px 28px rgba(0,76,70,.16) !important;
      border-bottom: 0 !important;
    }
    .loop-ref-header-inner {
      min-height: 64px !important;
      padding-top: 7px !important;
      padding-bottom: 7px !important;
      grid-template-columns: 166px minmax(420px, 680px) auto !important;
    }
    .loop-ref-search-input {
      height: 45px !important;
      border-radius: 999px !important;
      background: rgba(255,250,242,.96) !important;
      border-color: rgba(255,250,242,.70) !important;
      box-shadow: 0 8px 22px rgba(15,23,42,.10) !important;
    }
    .loop-ref-nav-btn { height: 36px !important; font-size: 13.2px !important; font-weight: 780 !important; color: rgba(255,250,242,.96) !important; }
    .loop-ref-bell, .loop-ref-avatar { width: 40px !important; height: 40px !important; background: rgba(255,250,242,.96) !important; box-shadow: 0 8px 20px rgba(15,23,42,.12) !important; }
  }

  /* LOOP PHASE 4A HEADER — darker emerald, warm hotel aesthetic */
  @media (min-width: 921px) {
    .loop-ref-header{min-height:66px!important;background-color:#134f47!important;background-image:radial-gradient(circle at 16% 0%,rgba(255,248,238,.13),transparent 26%),linear-gradient(135deg,#103f39 0%,#13594f 58%,#1d6b61 100%)!important;border-bottom: 0 !important;box-shadow:0 14px 34px rgba(16,63,57,.20)!important;backdrop-filter:blur(18px) saturate(1.04)!important}
    .loop-ref-header-inner{min-height:66px!important;padding-top:8px!important;padding-bottom:8px!important;width:min(100%,1540px)!important;grid-template-columns:170px minmax(420px,680px) auto!important;gap:clamp(16px,2vw,28px)!important}
    .loop-ref-search-input{height:46px!important;border-radius:999px!important;border:1px solid rgba(255,248,238,.72)!important;background:rgba(255,248,238,.95)!important;color:#14120e!important;box-shadow:0 10px 24px rgba(20,17,13,.13)!important;font-weight:690!important}
    .loop-ref-search-input::placeholder{color:#746855!important;opacity:.82!important;font-weight:590!important}
    .loop-ref-nav-btn{height:36px!important;color:rgba(255,248,238,.96)!important;font-size:13.2px!important;font-weight:760!important}
    .loop-ref-bell,.loop-ref-avatar{width:40px!important;height:40px!important;background:rgba(255,248,238,.95)!important;color:#134f47!important;border-color:rgba(255,248,238,.62)!important;box-shadow:0 10px 22px rgba(20,17,13,.14)!important}
    .loop-ref-bell svg{color:#134f47!important}
  }

  /* LOOP PHASE 5 HEADER — warm deep emerald luxury hotel */
  @media (min-width: 921px) {
    .loop-ref-header {
      min-height: 68px !important;
      background-color: #134f47 !important;
      background-image:
        radial-gradient(circle at 10% 0%, rgba(255,248,238,.12), transparent 28%),
        linear-gradient(135deg, #103f39 0%, #14554d 56%, #1d6b61 100%) !important;
      border-bottom: 0 !important;
      box-shadow: 0 16px 38px rgba(16,63,57,.20) !important;
    }

    .loop-ref-header-inner {
      min-height: 68px !important;
      padding-top: 8px !important;
      padding-bottom: 8px !important;
      width: min(100%, 1540px) !important;
      grid-template-columns: 172px minmax(420px, 690px) auto !important;
    }

    .loop-ref-search-input {
      height: 46px !important;
      border-radius: 999px !important;
      border: 1px solid rgba(255,248,238,.72) !important;
      background: rgba(255,248,238,.96) !important;
      color: #14120e !important;
      box-shadow: 0 10px 24px rgba(20,17,13,.12) !important;
    }

    .loop-ref-search-input::placeholder {
      color: #746855 !important;
      opacity: .84 !important;
    }

    .loop-ref-nav-btn {
      height: 38px !important;
      color: rgba(255,248,238,.96) !important;
      font-weight: 780 !important;
      font-size: 13.5px !important;
    }

    .loop-ref-bell,
    .loop-ref-avatar {
      width: 40px !important;
      height: 40px !important;
      background: rgba(255,248,238,.96) !important;
      color: #134f47 !important;
      border-color: rgba(255,248,238,.62) !important;
      box-shadow: 0 10px 24px rgba(20,17,13,.13) !important;
    }
  }

  /* LOOP PHASE 5B HEADER — warm deep emerald, subtle hotel */
  @media (min-width: 921px) {
    .loop-ref-header {
      min-height: 64px !important;
      background-color: #134f47 !important;
      background-image: linear-gradient(135deg, #103f39 0%, #14554d 58%, #1d6b61 100%) !important;
      box-shadow: 0 12px 30px rgba(16,63,57,.18) !important;
      border-bottom: 0 !important;
    }

    .loop-ref-header-inner {
      min-height: 64px !important;
      padding-top: 7px !important;
      padding-bottom: 7px !important;
    }

    .loop-ref-search-input {
      height: 44px !important;
      border-radius: 999px !important;
      background: rgba(255,248,238,.96) !important;
      border-color: rgba(255,248,238,.70) !important;
      box-shadow: 0 8px 22px rgba(20,17,13,.11) !important;
    }
  }

  /* =========================================================
     PHASE 6A — ADAPTIVE HEADER FIT SYSTEM
     Goal: every viewport gets a stable header without hidden buttons.
     - Desktop keeps full labels.
     - Tablet converts nav labels to icon-only and limits search width.
     - Admin calendar/tablet hides global search because the calendar toolbar is the priority.
     - Small web/mobile browser uses a two-row shell unless calendar context requests compact mode.
     ========================================================= */

  .loop-ref-header,
  .loop-ref-header * {
    box-sizing: border-box !important;
  }

  .loop-ref-header-inner,
  .loop-ref-search-wrap,
  .loop-ref-actions {
    min-width: 0 !important;
  }

  .loop-ref-actions {
    flex-wrap: nowrap !important;
    overflow: visible !important;
  }

  .loop-ref-actions > * {
    flex-shrink: 0 !important;
  }

  @media (min-width: 1181px) {
    .loop-ref-header-inner {
      grid-template-columns: clamp(132px, 11vw, 172px) minmax(320px, min(42vw, 690px)) auto !important;
      gap: clamp(14px, 1.7vw, 28px) !important;
    }

    .loop-ref-search-wrap {
      width: 100% !important;
      max-width: 690px !important;
    }
  }

  @media (min-width: 921px) and (max-width: 1180px) {
    .loop-ref-header {
      min-height: 58px !important;
      height: auto !important;
      overflow: visible !important;
    }

    .loop-ref-header-inner {
      min-height: 58px !important;
      padding: 7px 12px !important;
      grid-template-columns: 118px minmax(180px, 1fr) auto !important;
      gap: 9px !important;
      width: 100% !important;
      max-width: 100% !important;
    }

    .loop-ref-logo-button > div {
      height: 40px !important;
      max-width: 112px !important;
    }

    .loop-ref-logo-button img {
      max-height: 38px !important;
      max-width: 112px !important;
    }

    .loop-ref-search-wrap {
      width: 100% !important;
      max-width: 380px !important;
      justify-self: stretch !important;
    }

    .loop-ref-search-input {
      height: 40px !important;
      max-height: 40px !important;
      border-radius: 999px !important;
      padding-left: 40px !important;
      padding-right: 38px !important;
      font-size: 12.5px !important;
      box-shadow: 0 7px 18px rgba(15,23,42,.10) !important;
    }

    .loop-ref-icon-left {
      left: 14px !important;
    }

    .loop-ref-actions {
      gap: 6px !important;
      justify-self: end !important;
    }

    .loop-ref-nav-btn {
      width: 38px !important;
      height: 38px !important;
      min-width: 38px !important;
      min-height: 38px !important;
      padding: 0 !important;
      border-radius: 999px !important;
      background: rgba(255,255,255,.12) !important;
      border-color: rgba(255,255,255,.18) !important;
    }

    .loop-ref-nav-btn span:not(.loop-ref-badge) {
      display: none !important;
    }

    .loop-ref-nav-btn svg {
      width: 19px !important;
      height: 19px !important;
    }

    .loop-ref-bell,
    .loop-ref-avatar {
      width: 38px !important;
      height: 38px !important;
      min-width: 38px !important;
      min-height: 38px !important;
    }

    .loop-ref-bell svg {
      width: 19px !important;
      height: 19px !important;
    }
  }

  @media (min-width: 768px) and (max-width: 1180px) {
    .loop-ref-header.loop-header-admin-context .loop-ref-header-inner {
      grid-template-columns: 116px 1fr auto !important;
    }

    .loop-ref-header.loop-header-admin-context .loop-ref-search-wrap {
      display: none !important;
    }

    .loop-ref-header.loop-header-admin-context .loop-ref-actions {
      grid-column: 3 !important;
    }
  }

  @media (max-width: 920px) {
    .loop-ref-header-inner {
      grid-template-columns: minmax(92px, auto) 1fr auto !important;
      grid-template-rows: 42px 50px !important;
      grid-template-areas:
        "logo spacer actions"
        "search search search" !important;
      min-height: 112px !important;
      padding: 8px 10px 10px !important;
      gap: 8px !important;
    }

    .loop-ref-actions {
      max-width: 100% !important;
      gap: 5px !important;
    }

    .loop-ref-nav-btn,
    .loop-ref-bell,
    .loop-ref-avatar {
      width: 36px !important;
      height: 36px !important;
      min-width: 36px !important;
      min-height: 36px !important;
      flex-basis: 36px !important;
    }

    .loop-ref-nav-btn svg,
    .loop-ref-bell svg {
      width: 18px !important;
      height: 18px !important;
    }

    .loop-ref-search-input {
      height: 48px !important;
      max-height: 48px !important;
      border-radius: 16px !important;
      font-size: 13px !important;
    }
  }

  @media (max-width: 920px) {
    .loop-ref-header.loop-header-admin-context {
      min-height: 58px !important;
    }

    .loop-ref-header.loop-header-admin-context .loop-ref-header-inner {
      min-height: 58px !important;
      grid-template-columns: 104px 1fr auto !important;
      grid-template-rows: 42px !important;
      grid-template-areas: "logo spacer actions" !important;
      padding: 8px 10px !important;
    }

    .loop-ref-header.loop-header-admin-context .loop-ref-search-wrap {
      display: none !important;
    }
  }

  @media (max-width: 390px) {
    .loop-ref-header-inner {
      grid-template-columns: minmax(80px, auto) 1fr auto !important;
      padding-left: 8px !important;
      padding-right: 8px !important;
    }

    .loop-ref-logo-button > div {
      max-width: 96px !important;
    }

    .loop-ref-logo-button img {
      max-width: 96px !important;
    }

    .loop-ref-actions {
      gap: 4px !important;
    }

    .loop-ref-nav-btn,
    .loop-ref-bell,
    .loop-ref-avatar {
      width: 34px !important;
      height: 34px !important;
      min-width: 34px !important;
      min-height: 34px !important;
      flex-basis: 34px !important;
    }
  }


  /* =========================================================
     LOOP HEADER — CONSISTENT BRAND COLOR FIX
     Keeps the same emerald header identity across desktop/tablet/mobile.
     Mobile remains compact, but no longer switches to white/cream.
     ========================================================= */

  :root {
    --loop-header-brand: #134f47;
    --loop-header-brand-2: #14554d;
    --loop-header-brand-3: #1d6b61;
    --loop-header-surface: rgba(255, 248, 238, .96);
    --loop-header-safe-top: max(env(safe-area-inset-top, 0px), 0px);
  }

  .loop-ref-header,
  #app-main-header {
    background-color: var(--loop-header-brand) !important;
    background-image:
      radial-gradient(circle at 10% 0%, rgba(255,248,238,.12), transparent 28%),
      linear-gradient(135deg, var(--loop-header-brand) 0%, var(--loop-header-brand-2) 56%, var(--loop-header-brand-3) 100%) !important;
    border-bottom: 0 !important;
    box-shadow: 0 14px 34px rgba(16,63,57,.20) !important;
  }

  .loop-ref-header-inner {
    background: transparent !important;
  }

  .loop-ref-logo-button,
  .loop-ref-actions,
  .loop-ref-nav-btn {
    background-color: transparent !important;
  }

  .loop-ref-nav-btn {
    color: rgba(255,248,238,.96) !important;
  }

  .loop-ref-nav-btn svg {
    color: rgba(255,248,238,.96) !important;
    stroke: currentColor !important;
  }

  .loop-ref-bell,
  .loop-ref-avatar {
    background: var(--loop-header-surface) !important;
    color: var(--loop-header-brand) !important;
    border-color: rgba(255,248,238,.62) !important;
  }

  .loop-ref-bell svg {
    color: var(--loop-header-brand) !important;
    stroke: currentColor !important;
  }

  .loop-ref-search-input {
    background: var(--loop-header-surface) !important;
    border-color: rgba(255,248,238,.72) !important;
    color: #14120e !important;
  }

  .loop-ref-search-input::placeholder {
    color: #746855 !important;
    opacity: .84 !important;
  }

  @media (max-width: 920px) {
    .loop-ref-header,
    #app-main-header {
      min-height: calc(64px + var(--loop-header-safe-top)) !important;
      height: auto !important;
      padding-top: var(--loop-header-safe-top) !important;
      background-color: var(--loop-header-brand) !important;
      background-image:
        radial-gradient(circle at 10% 0%, rgba(255,248,238,.12), transparent 28%),
        linear-gradient(135deg, var(--loop-header-brand) 0%, var(--loop-header-brand-2) 56%, var(--loop-header-brand-3) 100%) !important;
      box-shadow: 0 10px 26px rgba(16,63,57,.18) !important;
      overflow: visible !important;
    }

    .loop-ref-header-inner {
      min-height: 64px !important;
      height: 64px !important;
      padding: 9px 12px !important;
      display: grid !important;
      grid-template-columns: auto 1fr auto !important;
      grid-template-rows: 46px !important;
      grid-template-areas: "logo spacer actions" !important;
      align-items: center !important;
      gap: 10px !important;
      background: transparent !important;
    }

    .loop-ref-logo-button {
      grid-area: logo !important;
      height: 46px !important;
      max-height: 46px !important;
      overflow: visible !important;
    }

    .loop-ref-logo-button > div {
      height: 46px !important;
      max-height: 46px !important;
      max-width: 118px !important;
      overflow: visible !important;
    }

    .loop-ref-logo-button img {
      max-height: 38px !important;
      max-width: 118px !important;
      object-fit: contain !important;
      filter: brightness(0) invert(1) opacity(.96) !important;
    }

    .loop-ref-search-wrap {
      display: none !important;
    }

    .loop-ref-actions {
      grid-area: actions !important;
      height: 46px !important;
      max-height: 46px !important;
      justify-self: end !important;
      align-self: center !important;
      display: flex !important;
      align-items: center !important;
      justify-content: flex-end !important;
      gap: 8px !important;
      background: transparent !important;
    }

    .loop-ref-nav-btn {
      width: 42px !important;
      height: 42px !important;
      min-width: 42px !important;
      min-height: 42px !important;
      flex: 0 0 42px !important;
      border-radius: 999px !important;
      background: rgba(255,248,238,.12) !important;
      border: 1px solid rgba(255,248,238,.18) !important;
      color: rgba(255,248,238,.96) !important;
      box-shadow: none !important;
    }

    .loop-ref-nav-btn span:not(.loop-ref-badge) {
      display: none !important;
    }

    .loop-ref-bell,
    .loop-ref-avatar {
      width: 42px !important;
      height: 42px !important;
      min-width: 42px !important;
      min-height: 42px !important;
      flex: 0 0 42px !important;
      background: var(--loop-header-surface) !important;
      color: var(--loop-header-brand) !important;
      border: 1px solid rgba(255,248,238,.62) !important;
      box-shadow: 0 8px 20px rgba(20,17,13,.14) !important;
    }

    .loop-ref-badge {
      background: #fffaf2 !important;
      color: var(--loop-header-brand) !important;
      border-color: var(--loop-header-brand) !important;
    }

    .loop-native-mobile-header,
    .loop-mobile-header,
    .loop-header-mobile-shell,
    .loop-compact-mobile-header {
      background-color: var(--loop-header-brand) !important;
      background-image:
        radial-gradient(circle at 10% 0%, rgba(255,248,238,.12), transparent 28%),
        linear-gradient(135deg, var(--loop-header-brand) 0%, var(--loop-header-brand-2) 56%, var(--loop-header-brand-3) 100%) !important;
      border-bottom: 0 !important;
      box-shadow: 0 10px 26px rgba(16,63,57,.18) !important;
    }
  }

  @media (max-width: 420px) {
    .loop-ref-header-inner {
      padding-left: 10px !important;
      padding-right: 10px !important;
      gap: 8px !important;
    }

    .loop-ref-actions {
      gap: 7px !important;
    }

    .loop-ref-nav-btn,
    .loop-ref-bell,
    .loop-ref-avatar {
      width: 40px !important;
      height: 40px !important;
      min-width: 40px !important;
      min-height: 40px !important;
      flex-basis: 40px !important;
    }
  }



  /* LOOP PHASE 1X GLOBAL HEADER BORDER SWEEP */
  #app-main-header,
  #app-main-header.loop-ref-header,
  #app-main-header.loop-mobile-header-v2,
  .loop-ref-header,
  .loop-mobile-header-v2 {
    border-bottom: 0 !important;
    box-shadow: none !important;
    outline: 0 !important;
  }
  #app-main-header::before,
  #app-main-header::after,
  .loop-ref-header::before,
  .loop-ref-header::after,
  .loop-mobile-header-v2::before,
  .loop-mobile-header-v2::after {
    content: none !important;
    display: none !important;
    border: 0 !important;
    box-shadow: none !important;
  }


  /* =========================================================
     LOOP HEADER GLOBAL STABILITY LOCK — desktop/tablet web
     Source of truth for the global header.
     Keeps the header in normal document flow, sticky, fixed height,
     and prevents older phase CSS from changing layout while scrolling/zooming.
     ========================================================= */
  :root {
    --loop-global-header-h: 66px;
    --loop-global-header-mobile-h: calc(env(safe-area-inset-top, 0px) + 58px);
    --loop-header-emerald-a: #103f39;
    --loop-header-emerald-b: #14554d;
    --loop-header-emerald-c: #1d6b61;
    --loop-header-cream: rgba(255,248,238,.96);
  }

  html {
    scroll-padding-top: var(--loop-global-header-h) !important;
  }

  body {
    overflow-x: hidden !important;
  }

  #app-main-header.loop-ref-header,
  .loop-ref-header {
    position: sticky !important;
    top: 0 !important;
    left: auto !important;
    right: auto !important;
    z-index: var(--loop-z-header, 1000) !important;
    width: 100% !important;
    height: var(--loop-global-header-h) !important;
    min-height: var(--loop-global-header-h) !important;
    max-height: var(--loop-global-header-h) !important;
    padding-top: 0 !important;
    margin: 0 !important;
    display: block !important;
    flex: 0 0 auto !important;
    background-color: var(--loop-header-emerald-b) !important;
    background-image:
      radial-gradient(circle at 10% 0%, rgba(255,248,238,.12), transparent 28%),
      linear-gradient(135deg, var(--loop-header-emerald-a) 0%, var(--loop-header-emerald-b) 56%, var(--loop-header-emerald-c) 100%) !important;
    border: 0 !important;
    border-bottom: 0 !important;
    box-shadow: 0 12px 30px rgba(16,63,57,.20) !important;
    overflow: visible !important;
    transform: translateZ(0) !important;
    contain: layout style paint !important;
    isolation: isolate !important;
  }

  #app-main-header.loop-ref-header *,
  .loop-ref-header * {
    box-sizing: border-box !important;
  }

  .loop-ref-header-inner {
    width: min(100%, 1540px) !important;
    height: var(--loop-global-header-h) !important;
    min-height: var(--loop-global-header-h) !important;
    max-height: var(--loop-global-header-h) !important;
    margin: 0 auto !important;
    padding: 7px clamp(18px, 3vw, 46px) !important;
    display: grid !important;
    grid-template-columns: 160px minmax(360px, 680px) minmax(0, auto) !important;
    grid-template-rows: 52px !important;
    align-items: center !important;
    gap: clamp(14px, 2vw, 28px) !important;
    background: transparent !important;
    overflow: visible !important;
  }

  .loop-ref-logo-button {
    height: 52px !important;
    max-height: 52px !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: flex-start !important;
    overflow: visible !important;
  }

  .loop-ref-logo-button img {
    max-height: 42px !important;
    width: auto !important;
    object-fit: contain !important;
    display: block !important;
  }

  .loop-ref-search-wrap {
    width: 100% !important;
    min-width: 0 !important;
    max-width: 680px !important;
    height: 52px !important;
    display: block !important;
    align-self: center !important;
    overflow: visible !important;
  }

  .loop-ref-search-wrap form,
  .loop-ref-search-wrap > form {
    position: relative !important;
    width: 100% !important;
    min-width: 0 !important;
    height: 52px !important;
    display: block !important;
    margin: 0 !important;
    padding: 0 !important;
    overflow: visible !important;
  }

  .loop-ref-search-input {
    width: 100% !important;
    height: 46px !important;
    min-height: 46px !important;
    max-height: 46px !important;
    border-radius: 999px !important;
    border: 1px solid rgba(255,248,238,.72) !important;
    background: var(--loop-header-cream) !important;
    color: #14120e !important;
    box-shadow: 0 10px 24px rgba(20,17,13,.13) !important;
    padding: 0 46px 0 50px !important;
    font-size: 14px !important;
    font-weight: 700 !important;
    line-height: 46px !important;
  }

  .loop-ref-icon-left {
    left: 18px !important;
    top: 50% !important;
    transform: translateY(-50%) !important;
  }

  .loop-ref-actions {
    height: 52px !important;
    min-width: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: flex-end !important;
    gap: 12px !important;
    overflow: visible !important;
    background: transparent !important;
  }

  .loop-ref-nav-btn {
    height: 40px !important;
    min-height: 40px !important;
    max-height: 40px !important;
    border-radius: 999px !important;
    color: rgba(255,248,238,.96) !important;
    background: transparent !important;
    padding: 0 8px !important;
    font-size: 14px !important;
    font-weight: 820 !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 8px !important;
    white-space: nowrap !important;
  }

  .loop-ref-bell,
  .loop-ref-avatar {
    width: 42px !important;
    height: 42px !important;
    min-width: 42px !important;
    min-height: 42px !important;
    max-width: 42px !important;
    max-height: 42px !important;
    flex: 0 0 42px !important;
    border-radius: 999px !important;
    background: var(--loop-header-cream) !important;
    color: #134f47 !important;
    border: 1px solid rgba(255,248,238,.62) !important;
    box-shadow: 0 10px 22px rgba(20,17,13,.14) !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    margin: 0 !important;
    transform: none !important;
  }

  .loop-ref-bell svg {
    color: #134f47 !important;
  }

  .loop-ref-badge {
    z-index: 2 !important;
  }

  @media (max-width: 1220px) and (min-width: 921px) {
    .loop-ref-header-inner {
      grid-template-columns: 132px minmax(280px, 1fr) auto !important;
      gap: 12px !important;
      padding-left: 16px !important;
      padding-right: 16px !important;
    }

    .loop-ref-search-wrap {
      max-width: none !important;
    }

    .loop-ref-actions {
      gap: 8px !important;
    }

    .loop-ref-nav-btn {
      width: 42px !important;
      min-width: 42px !important;
      padding: 0 !important;
    }

    .loop-ref-nav-btn span:not(.loop-ref-badge) {
      display: none !important;
    }
  }

  @media (max-width: 920px) {
    html {
      scroll-padding-top: var(--loop-global-header-mobile-h) !important;
    }

    #app-main-header.loop-ref-header,
    .loop-ref-header {
      height: var(--loop-global-header-mobile-h) !important;
      min-height: var(--loop-global-header-mobile-h) !important;
      max-height: var(--loop-global-header-mobile-h) !important;
      padding-top: env(safe-area-inset-top, 0px) !important;
    }

    .loop-ref-header-inner {
      height: 58px !important;
      min-height: 58px !important;
      max-height: 58px !important;
      padding: 8px 12px !important;
      grid-template-columns: auto 1fr auto !important;
      grid-template-rows: 42px !important;
      grid-template-areas: "logo spacer actions" !important;
      gap: 10px !important;
    }

    .loop-ref-search-wrap {
      display: none !important;
    }

    .loop-ref-logo-button {
      grid-area: logo !important;
      height: 42px !important;
      max-height: 42px !important;
    }

    .loop-ref-actions {
      grid-area: actions !important;
      height: 42px !important;
      gap: 7px !important;
    }

    .loop-ref-nav-btn,
    .loop-ref-bell,
    .loop-ref-avatar {
      width: 40px !important;
      height: 40px !important;
      min-width: 40px !important;
      min-height: 40px !important;
      max-width: 40px !important;
      max-height: 40px !important;
      flex: 0 0 40px !important;
      padding: 0 !important;
    }

    .loop-ref-nav-btn span:not(.loop-ref-badge) {
      display: none !important;
    }
  }



  /* =========================================================
     LOOP HEADER GLOBAL FIXED LOCK — Facebook/LinkedIn behavior
     Sticky can disappear when a parent scroll container clips it.
     This forces the global header to be fixed to the viewport.
     ========================================================= */
  :root {
    --loop-global-header-h: 66px;
    --loop-global-header-mobile-h: calc(env(safe-area-inset-top, 0px) + 58px);
  }

  html {
    scroll-padding-top: var(--loop-global-header-h) !important;
  }

  body {
    padding-top: var(--loop-global-header-h) !important;
    overflow-x: hidden !important;
  }

  #app-main-header.loop-ref-header,
  .loop-ref-header {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: auto !important;
    z-index: var(--loop-z-header, 1000) !important;
    width: 100vw !important;
    height: var(--loop-global-header-h) !important;
    min-height: var(--loop-global-header-h) !important;
    max-height: var(--loop-global-header-h) !important;
    margin: 0 !important;
    padding-top: 0 !important;
    display: block !important;
    flex: 0 0 auto !important;
    overflow: visible !important;
    transform: translateZ(0) !important;
    will-change: transform !important;
    contain: layout style paint !important;
    isolation: isolate !important;
  }

  .loop-ref-header-inner {
    height: var(--loop-global-header-h) !important;
    min-height: var(--loop-global-header-h) !important;
    max-height: var(--loop-global-header-h) !important;
  }

  .loop-ref-header + .loop-mobile-spacer-v2,
  #app-main-header + .loop-mobile-spacer-v2 {
    display: none !important;
    height: 0 !important;
    min-height: 0 !important;
    max-height: 0 !important;
  }

  @media (max-width: 920px) {
    html {
      scroll-padding-top: var(--loop-global-header-mobile-h) !important;
    }

    body {
      padding-top: var(--loop-global-header-mobile-h) !important;
    }

    #app-main-header.loop-ref-header,
    .loop-ref-header {
      height: var(--loop-global-header-mobile-h) !important;
      min-height: var(--loop-global-header-mobile-h) !important;
      max-height: var(--loop-global-header-mobile-h) !important;
      padding-top: env(safe-area-inset-top, 0px) !important;
    }
  }


  /* LOOP SEARCH MINIMAL WARM REVAMP */
  .loop-search-suggestions-layer {
    left: 0 !important;
    right: 0 !important;
    filter: drop-shadow(0 24px 44px rgba(15,23,42,.13)) !important;
  }
  .loop-ref-search-input {
    -webkit-appearance: none !important;
    appearance: none !important;
  }
  .loop-ref-search-input::-webkit-search-decoration,
  .loop-ref-search-input::-webkit-search-cancel-button,
  .loop-ref-search-input::-webkit-search-results-button,
  .loop-ref-search-input::-webkit-search-results-decoration {
    display: none !important;
  }
  @media (max-width: 920px) {
    .loop-mobile-search-results-v2 {
      padding-top: 8px !important;
    }
  }


  /* LOOP SEARCH PORTAL FIX — keep suggestions above hero */
  .loop-search-suggestions-portal {
    pointer-events: auto !important;
    isolation: isolate !important;
  }
  .loop-search-suggestions-portal .loop-warm-search-panel {
    position: relative !important;
    z-index: 1 !important;
  }


  /* LOOP SEARCH PORTAL ALIGNMENT FIX — dropdown centered under input */
  .loop-search-suggestions-layer.loop-search-suggestions-portal {
    position: fixed !important;
    top: var(--loop-search-portal-top) !important;
    left: var(--loop-search-portal-left) !important;
    right: auto !important;
    width: var(--loop-search-portal-width) !important;
    max-width: calc(100vw - 36px) !important;
    margin: 0 !important;
    z-index: var(--loop-z-header-panel, 1200) !important;
    filter: drop-shadow(0 24px 44px rgba(15,23,42,.13)) !important;
    pointer-events: auto !important;
    isolation: isolate !important;
  }

  .loop-search-suggestions-layer.loop-search-suggestions-portal .loop-warm-search-panel {
    width: 100% !important;
    max-width: 100% !important;
    border-radius: 22px !important;
  }

  @media (max-width: 760px) {
    .loop-search-suggestions-layer.loop-search-suggestions-portal {
      left: 12px !important;
      right: 12px !important;
      width: auto !important;
      max-width: none !important;
    }
  }


  /* =========================================================
     LOOP HEADER V46 — DESKTOP/TABLET NAV FIT LOCK
     Prevents Calendar / My Bookings from wrapping under the search bar
     in the awkward desktop breakpoint.
     ========================================================= */
  @media (min-width: 921px) and (max-width: 1380px) {
    /* The body clearance must match this breakpoint's actual header height. */
    :root {
      --loop-global-header-h: 64px;
    }

    #app-main-header.loop-ref-header,
    .loop-ref-header {
      height: 64px !important;
      min-height: 64px !important;
      max-height: 64px !important;
      overflow: visible !important;
    }

    .loop-ref-header-inner {
      width: 100% !important;
      max-width: 100% !important;
      height: 64px !important;
      min-height: 64px !important;
      max-height: 64px !important;
      padding: 7px clamp(12px, 1.7vw, 24px) !important;
      grid-template-columns: 132px minmax(260px, 1fr) auto !important;
      grid-template-rows: 50px !important;
      align-items: center !important;
      column-gap: clamp(10px, 1.5vw, 18px) !important;
      overflow: visible !important;
    }

    .loop-ref-logo-button,
    .loop-ref-logo-button > div {
      height: 50px !important;
      max-height: 50px !important;
      max-width: 118px !important;
    }

    .loop-ref-logo-button img {
      max-height: 38px !important;
      max-width: 118px !important;
    }

    .loop-ref-search-wrap {
      width: 100% !important;
      min-width: 0 !important;
      max-width: none !important;
      height: 50px !important;
      align-self: center !important;
    }

    .loop-ref-search-wrap form,
    .loop-ref-search-wrap > form {
      height: 50px !important;
      min-width: 0 !important;
    }

    .loop-ref-search-input {
      height: 44px !important;
      min-height: 44px !important;
      max-height: 44px !important;
      line-height: 44px !important;
      font-size: 13.5px !important;
      padding-left: 46px !important;
      padding-right: 42px !important;
    }

    .loop-ref-actions {
      height: 50px !important;
      max-height: 50px !important;
      display: flex !important;
      align-items: center !important;
      justify-content: flex-end !important;
      flex-wrap: nowrap !important;
      gap: 8px !important;
      min-width: max-content !important;
      overflow: visible !important;
    }

    .loop-ref-actions > * {
      flex-shrink: 0 !important;
    }

    .loop-ref-nav-btn {
      width: 42px !important;
      height: 42px !important;
      min-width: 42px !important;
      min-height: 42px !important;
      max-width: 42px !important;
      max-height: 42px !important;
      flex: 0 0 42px !important;
      padding: 0 !important;
      gap: 0 !important;
      border-radius: 999px !important;
      background: rgba(255,248,238,.12) !important;
      border: 1px solid rgba(255,248,238,.18) !important;
      overflow: visible !important;
    }

    .loop-ref-nav-btn span:not(.loop-ref-badge) {
      display: none !important;
    }

    .loop-ref-nav-btn svg {
      width: 19px !important;
      height: 19px !important;
      flex: 0 0 auto !important;
    }

    .loop-ref-bell,
    .loop-ref-avatar {
      width: 42px !important;
      height: 42px !important;
      min-width: 42px !important;
      min-height: 42px !important;
      max-width: 42px !important;
      max-height: 42px !important;
      flex: 0 0 42px !important;
      align-self: center !important;
    }
  }

  @media (min-width: 1381px) {
    .loop-ref-actions {
      flex-wrap: nowrap !important;
      align-items: center !important;
      overflow: visible !important;
    }

    .loop-ref-nav-btn {
      flex: 0 0 auto !important;
      white-space: nowrap !important;
    }
  }


  /* =========================================================
     LOOP GLOBAL MODAL / HEADER CONTRACT R8
     Final override after all historical header phases.
     The backdrop dims the header; the header itself is never hidden.
     ========================================================= */
  #app-main-header.loop-header-modal-covered,
  #app-main-header.loop-ref-header.loop-header-modal-covered,
  #app-main-header.loop-mobile-header-v2.loop-header-modal-covered {
    opacity: 1 !important;
    visibility: visible !important;
    pointer-events: none !important;
    border: 0 !important;
    border-bottom: 0 !important;
    outline: 0 !important;
    box-shadow: none !important;
  }

  #app-main-header.loop-header-modal-covered::before,
  #app-main-header.loop-header-modal-covered::after {
    content: none !important;
    display: none !important;
    border: 0 !important;
    box-shadow: none !important;
  }

  @supports selector(body:has([aria-modal="true"])) {
    html body:has(
      .loop-accessible-modal-layer,
      .gen-v3-modal-workspace,
      .loop-cropper-backdrop,
      .loop-layer-modal-backdrop,
      .cp-dup-backdrop,
      [aria-modal="true"]
    ) #app-main-header {
      opacity: 1 !important;
      visibility: visible !important;
      pointer-events: none !important;
      border: 0 !important;
      border-bottom: 0 !important;
      outline: 0 !important;
      box-shadow: none !important;
    }
  }

`;

function SvgBase({ children, size = 18, strokeWidth = 2 }) {
  return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>);
}
function PinIconSvg() { return <SvgBase><path d="M12 21s7-5.1 7-12a7 7 0 1 0-14 0c0 6.9 7 12 7 12Z" /><circle cx="12" cy="9" r="2.4" /></SvgBase>; }
function BusinessIconSvg() { return <SvgBase><path d="M3 21h18" /><path d="M5 21V7l7-4 7 4v14" /><path d="M9 21v-6h6v6" /></SvgBase>; }
function CalendarIconSvg() { return <SvgBase><path d="M8 2v4" /><path d="M16 2v4" /><path d="M3.5 9.5h17" /><path d="M5.5 5h13A2.5 2.5 0 0 1 21 7.5v11A2.5 2.5 0 0 1 18.5 21h-13A2.5 2.5 0 0 1 3 18.5v-11A2.5 2.5 0 0 1 5.5 5Z" /></SvgBase>; }
function BookmarkIconSvg() { return <SvgBase><path d="M6.5 4.5A2.5 2.5 0 0 1 9 2h6a2.5 2.5 0 0 1 2.5 2.5V21L12 17.8 6.5 21V4.5Z" /></SvgBase>; }
function BellIconSvg() { return <SvgBase><path d="M18 8a6 6 0 1 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" /><path d="M10 21h4" /></SvgBase>; }
function UserIconSvg() { return <SvgBase><path d="M20 21a8 8 0 1 0-16 0" /><circle cx="12" cy="7" r="4" /></SvgBase>; }

function getIsNativeRuntime() {
  try {
    const cap = (typeof window !== 'undefined' && window.Capacitor) ? window.Capacitor : null;
    if (!cap) return false;

    if (typeof cap.isNativePlatform === 'function') return cap.isNativePlatform();

    const platform = (typeof cap.getPlatform === 'function')
      ? cap.getPlatform()
      : (cap.platform || null);

    return !!(platform && platform !== 'web');
  } catch {
    return false;
  }
}

function LoopLogo({ alt = 'Loop', height = 62, width = 178, native = false }) {
  // Final logo system:
  // Always use the bundled asset imported inside Header.jsx.
  // Do not use logoSrc props, PUBLIC_URL, /logo.png, or text fallbacks.
  // This keeps the same image working across Vercel routes, web mobile, Android and iOS.
  const maxHeight = native ? 46 : height;
  const maxWidth = native ? 156 : width;
  const src = IMPORTED_LOGO || IMPORTED_LOGO_ALT;

  if (!src) return null;

  return (
    <img
      src={src}
      alt={alt}
      onError={(e) => {
        // Hide broken icon/alt text. Never append text fallback, because that caused "LoopLoop".
        try { e.currentTarget.style.display = 'none'; } catch (_) {}
      }}
      style={{
        height: maxHeight,
        width: 'auto',
        display: 'block',
        objectFit: 'contain',
        objectPosition: native ? 'center' : 'left center',
        maxHeight,
        maxWidth,
        minWidth: 0,
        flex: '0 0 auto',
      }}
      height={maxHeight}
      width={maxWidth}
    />
  );
}

function Header(props) {
  useLanguage();
  const {
    db: dbProp,
    logoWidth = 156,
    logoHeight = 50,
    q,
    setQ,
    onSearch,
    showSuggestions,
    setShowSuggestions,
    openAvatar,
    setOpenAvatar,
    pendingCount = 0,
    calendarPendingCount = 0,
    onHomeClick,
    onOpenBusinessSelector,
    onShowUserProfile,
    onAdminManage,
    onShowMyBookings,
    onShowMyLoyalty,
    onShowPending,
    onCreateBusiness,
    onLogout,
    userName,
    userEmail,
    isLoggedIn = false,
    authReady = true,
    onLogin,
    forceNativeLayout = false,
    onOpenCalendar,
    onSearchNearby,
    businessAccessOverride = null,
    ownsBusinessOverride = null,
    activeBusinessId: activeBusinessIdProp = "",
    activeBusinessName: activeBusinessNameProp = "",
  } = props;

  const navigate = useNavigate();
  const location = useLocation();
  const db = dbProp || defaultDb;

  const isNativeRuntime = getIsNativeRuntime();
  const [isCompactViewport, setIsCompactViewport] = useState(() => {
    try {
      return typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 920px)').matches;
    } catch (_) {
      return false;
    }
  });

  useEffect(() => {
    const updateCompactViewport = () => {
      try {
        setIsCompactViewport(Boolean(window.matchMedia && window.matchMedia('(max-width: 920px)').matches));
      } catch (_) {
        setIsCompactViewport(false);
      }
    };
    updateCompactViewport();
    window.addEventListener('resize', updateCompactViewport);
    window.addEventListener('orientationchange', updateCompactViewport);
    return () => {
      window.removeEventListener('resize', updateCompactViewport);
      window.removeEventListener('orientationchange', updateCompactViewport);
    };
  }, []);

  const useMobileLayout = isNativeRuntime || !!forceNativeLayout || isCompactViewport;
  useEffect(() => {
    if (!useMobileLayout) return undefined;
    if (typeof document === 'undefined') return undefined;

    const applyEmeraldHeaderLock = () => {
      try {
        const header = document.getElementById('app-main-header');
        if (!header) return;

        header.classList.add('loop-header-emerald-hardlock');
        header.style.setProperty('background-color', LOOP_HEADER_LOCK_BG_COLOR, 'important');
        header.style.setProperty('background-image', LOOP_HEADER_LOCK_BG_IMAGE, 'important');
        header.style.setProperty('background', LOOP_HEADER_LOCK_BG, 'important');
        const modalOpen = Boolean(document.querySelector(
          '.loop-accessible-modal-layer, .gen-v3-modal-workspace, .loop-cropper-backdrop, .loop-layer-modal-backdrop, .cp-dup-backdrop, [aria-modal="true"]'
        ));

        header.classList.toggle('loop-header-modal-covered', modalOpen);
        header.style.setProperty('border-bottom', '0', 'important');
        header.style.setProperty(
          'box-shadow',
          modalOpen ? 'none' : '0 12px 30px rgba(16,63,57,.20)',
          'important'
        );

        const row = header.querySelector('.loop-mobile-header-row');
        if (row && row.style) {
          row.style.setProperty('background', 'transparent', 'important');
          row.style.setProperty('background-color', 'transparent', 'important');
        }

        header.querySelectorAll('.loop-mobile-icon-btn-v2:not(.primary)').forEach((button) => {
          try {
            button.style.setProperty('background', 'rgba(255,248,238,.12)', 'important');
            button.style.setProperty('color', '#fffaf2', 'important');
            button.style.setProperty('border-color', 'rgba(255,248,238,.18)', 'important');
          } catch (_) {}
        });

        header.querySelectorAll('.loop-mobile-icon-btn-v2.primary').forEach((button) => {
          try {
            button.style.setProperty('background', 'rgba(255,248,238,.96)', 'important');
            button.style.setProperty('color', '#134f47', 'important');
            button.style.setProperty('border-color', 'rgba(255,248,238,.62)', 'important');
          } catch (_) {}
        });
      } catch (_) {}
    };

    applyEmeraldHeaderLock();

    let modalSyncFrame = null;
    const scheduleEmeraldHeaderLock = () => {
      try {
        if (modalSyncFrame) window.cancelAnimationFrame(modalSyncFrame);
        modalSyncFrame = window.requestAnimationFrame(() => {
          modalSyncFrame = null;
          applyEmeraldHeaderLock();
        });
      } catch (_) {
        applyEmeraldHeaderLock();
      }
    };

    const timeouts = [50, 180, 450, 900, 1500].map((ms) => window.setTimeout(applyEmeraldHeaderLock, ms));
    const modalObserver = new MutationObserver(scheduleEmeraldHeaderLock);
    try {
      modalObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style', 'aria-modal', 'aria-hidden'],
      });
    } catch (_) {}

    window.addEventListener('resize', scheduleEmeraldHeaderLock);
    window.addEventListener('orientationchange', scheduleEmeraldHeaderLock);

    return () => {
      timeouts.forEach((id) => {
        try { window.clearTimeout(id); } catch (_) {}
      });
      try { modalObserver.disconnect(); } catch (_) {}
      try {
        if (modalSyncFrame) window.cancelAnimationFrame(modalSyncFrame);
      } catch (_) {}
      window.removeEventListener('resize', scheduleEmeraldHeaderLock);
      window.removeEventListener('orientationchange', scheduleEmeraldHeaderLock);
    };
  }, [useMobileLayout]);

  const isHomeRoute = Boolean(
    location && (location.pathname === '/' || location.pathname === '' || location.pathname === '/index.html')
  );

  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [showMobileProfileSheet, setShowMobileProfileSheet] = useState(false);
  const [showLoginSheet, setShowLoginSheet] = useState(false);

  const [qLocal, setQLocal] = useState(q || '');
  useEffect(() => { if (typeof q === 'string') setQLocal(q); }, [q]);
  const effectiveQ = typeof q === 'string' ? q : qLocal;
  const setEffectiveQ = (val) => { if (typeof setQ === 'function') setQ(val); else setQLocal(val); };

  useEffect(() => {
    const term = new URLSearchParams(location.search).get('q');
    if (term !== null) { setQLocal(term); if (typeof setQ === 'function') setQ(term); }
  }, [location.search]);

  const { loading: locationLoading, search: searchLocations } = useLocationSuggestions(db);

  const locationSuggestions = useMemo(() => {
    if (!effectiveQ || !String(effectiveQ).trim()) return [];
    return searchLocations(effectiveQ);
  }, [effectiveQ, searchLocations]);

  const LOGO_SCALE = 1.3;
  const baseLogoHeight = logoHeight || 44;
  const baseLogoWidth = logoWidth || 130;
  const displayLogoHeight = Math.min(Math.round(baseLogoHeight * LOGO_SCALE), 88);
  const displayLogoWidth = Math.min(Math.round(baseLogoWidth * LOGO_SCALE), 320);
  const logoMaxWidth = displayLogoWidth;

  const cleanUserNameForHeader = String(userName || "").trim();
  const cleanUserEmailForHeader = String(userEmail || "").trim();
  const hasVisibleUserIdentity = Boolean(cleanUserNameForHeader || cleanUserEmailForHeader);
  // Do not show a generic "U" account unless the app has a real authenticated identity.
  // This prevents ghost sessions on iOS where cached profile data exists but Auth/manualAuth was cleared.
  const hasUser = Boolean(isLoggedIn === true && hasVisibleUserIdentity);
  const authHydrating = authReady === false;
  const userInitial = (cleanUserNameForHeader && cleanUserNameForHeader[0]) || (cleanUserEmailForHeader && cleanUserEmailForHeader[0]) || '';

  const avatarTriggerRef = useRef(null);
  const desktopSearchRef = useRef(null);
  const mobileSearchRef = useRef(null);
  const [desktopSearchPortalRect, setDesktopSearchPortalRect] = useState(null);

  const updateDesktopSearchPortalRect = useCallback(() => {
    try {
      const form = desktopSearchRef.current;
      if (!form || typeof window === 'undefined') {
        setDesktopSearchPortalRect(null);
        return;
      }

      const r = form.getBoundingClientRect();
      const viewportW = Math.max(320, window.innerWidth || document.documentElement?.clientWidth || 0);
      const margin = viewportW <= 760 ? 12 : 18;
      const maxPanelWidth = viewportW <= 760 ? viewportW - margin * 2 : 560;
      const width = Math.max(320, Math.min(Math.round(r.width), maxPanelWidth));
      const centeredLeft = Math.round(r.left + (r.width - width) / 2);
      const left = Math.max(margin, Math.min(centeredLeft, viewportW - width - margin));

      setDesktopSearchPortalRect({
        top: Math.round(r.bottom + 10),
        left,
        width,
      });
    } catch (_) {
      setDesktopSearchPortalRect(null);
    }
  }, []);

  useEffect(() => {
    if (!showSuggestions || showMobileSearch) return undefined;
    updateDesktopSearchPortalRect();
    const onMove = () => updateDesktopSearchPortalRect();
    try {
      window.addEventListener('resize', onMove);
      window.addEventListener('scroll', onMove, true);
      window.addEventListener('orientationchange', onMove);
    } catch (_) {}
    return () => {
      try {
        window.removeEventListener('resize', onMove);
        window.removeEventListener('scroll', onMove, true);
        window.removeEventListener('orientationchange', onMove);
      } catch (_) {}
    };
  }, [showSuggestions, showMobileSearch, updateDesktopSearchPortalRect]);

  useEffect(() => {
    const onAuthCleared = () => {
      try { if (typeof setOpenAvatar === "function") setOpenAvatar(false); } catch (_) {}
      try { setShowMobileProfileSheet(false); } catch (_) {}
      try { setShowLoginSheet(false); } catch (_) {}
      try {
        setBusinessAccessState({ loading: false, ownsBusiness: false, workerAccess: false, source: "auth-cleared" });
      } catch (_) {}
    };
    try {
      window.addEventListener("manual-auth-cleared", onAuthCleared);
      window.addEventListener("loop-auth-cleared", onAuthCleared);
    } catch (_) {}
    return () => {
      try { window.removeEventListener("manual-auth-cleared", onAuthCleared); } catch (_) {}
      try { window.removeEventListener("loop-auth-cleared", onAuthCleared); } catch (_) {}
    };
  }, [setOpenAvatar]);

  const rememberLoopSearch = (term) => {
    try {
      const clean = String(term || '').trim();
      if (!clean || typeof window === 'undefined' || !window.localStorage) return;
      const key = 'loop_search_recents_v2';
      const previous = JSON.parse(window.localStorage.getItem(key) || '[]');
      const next = [clean, ...(Array.isArray(previous) ? previous : []).filter((item) => String(item || '').trim().toLowerCase() !== clean.toLowerCase())].slice(0, 5);
      window.localStorage.setItem(key, JSON.stringify(next));
    } catch (_) {}
  };

  const closeSearchUI = ({ clearQuery = false, closeMobile = false } = {}) => {
    if (clearQuery) setEffectiveQ('');
    if (typeof setShowSuggestions === 'function') setShowSuggestions(false);
    if (closeMobile) setShowMobileSearch(false);
  };

  const handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const clean = String(effectiveQ || '').trim();
    if (!clean) {
      closeSearchUI({ closeMobile: true });
      return;
    }
    try {
      rememberLoopSearch(clean);
      if (typeof onSearch === 'function') onSearch(clean);
      else navigate(`/services/search?q=${encodeURIComponent(clean)}`);
      closeSearchUI({ closeMobile: true });
    } catch (err) { console.warn('Header onSearch failed', err); }
  };

  const handleLogoClick = () => {
    try { if (typeof onHomeClick === 'function') onHomeClick(); } catch (e) {}
    try {
      navigate('/?showRecommended=1', { state: { showRecommended: true } });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) { try { window.location.href = '/?showRecommended=1'; } catch (_) {} }
  };

  const handleCalendarClick = () => {
    if (!hasUser) { handleLoginClick(); return; }

    // LOOP GLOBAL CALENDAR SELECTOR — priority path.
    // A listener in App opens WorkerBusinessesModal as a portal above any screen.
    // If the listener calls preventDefault(), this click is considered handled.
    try {
      if (typeof window !== 'undefined') {
        const event = new CustomEvent('loop:open-calendar-selector', {
          detail: { source: 'header-calendar-button', path: location?.pathname || '' },
          cancelable: true,
        });
        const notPrevented = window.dispatchEvent(event);
        if (!notPrevented) return;
      }
    } catch (e) {
      try { console.warn('[Header] global calendar event failed', e); } catch (_) {}
    }

    if (typeof onOpenCalendar === 'function') {
      try { onOpenCalendar(); return; } catch (e) { console.warn('onOpenCalendar failed', e); }
    }

    // Calendar must always fall back to the universal selector route.
    // This is critical on iOS/Capacitor, where callbacks can hydrate after the header renders.
    try { navigate('/open-calendar'); return; } catch (e) {}

    if (typeof onOpenBusinessSelector === 'function') {
      try { onOpenBusinessSelector(); return; } catch (e) { console.warn('onOpenBusinessSelector failed', e); }
    }
    if (typeof onShowPending === 'function') {
      try { onShowPending(); return; } catch (e) { console.warn('onShowPending failed', e); }
    }
    try { window.location.href = '/open-calendar'; } catch (_) {}
  };

  const handleMyBookingsClick = () => {
    if (!hasUser) { handleLoginClick(); return; }
    if (typeof onShowMyBookings === 'function') {
      try { onShowMyBookings(); return; } catch (e) { console.warn('onShowMyBookings failed', e); }
    }
    try { navigate('/my-bookings'); } catch (e) { try { window.location.href = '/my-bookings'; } catch (_) {} }
  };

  const handleMyLoyaltyClick = () => {
    if (!hasUser) { handleLoginClick(); return; }
    if (typeof onShowMyLoyalty === 'function') {
      try { onShowMyLoyalty(); return; } catch (e) { console.warn('onShowMyLoyalty failed', e); }
    }
    try { navigate('/my-benefits'); } catch (e) { try { window.location.href = '/my-benefits'; } catch (_) {} }
  };

  const handleLoginClick = () => {
    try {
      if (useMobileLayout) {
        setShowLoginSheet(true);
        return;
      }

      if (typeof onLogin === 'function') { onLogin(); return; }
      navigate('/login');
    } catch (e) { try { window.location.href = '/login'; } catch (_) {} }
  };

  const runLoginAction = (target = '/login') => {
    try {
      setShowLoginSheet(false);

      if (target === '/login' && typeof onLogin === 'function') {
        onLogin();
        return;
      }

      navigate(target);
    } catch (e) {
      try { window.location.href = target; } catch (_) {}
    }
  };

  const pillWhite = {
    padding: '8px 14px',
    borderRadius: 9999,
    background: '#ffffff',
    color: PRIMARY_COLOR,
    border: '1px solid rgba(255,255,255,0.48)',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    cursor: 'pointer',
    fontWeight: 850,
    whiteSpace: 'nowrap',
    boxShadow: CONTROL_SHADOW,
    minHeight: 40,
  };
  const pillWhiteTextColor = PRIMARY_COLOR;

  const badgeStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 18,
    height: 18,
    padding: '0 6px',
    background: '#ef4444',
    color: '#ffffff',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    lineHeight: '18px',
    zIndex: 6,
    whiteSpace: 'nowrap',
  };

  const avatarSmallStyle = {
    width: NATIVE_CONTROL_H,
    height: NATIVE_CONTROL_H,
    borderRadius: 9999,
    background: '#ffffff',
    color: PRIMARY_COLOR,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    cursor: 'pointer',
    border: '1px solid rgba(255,255,255,0.48)',
    boxShadow: CONTROL_SHADOW,
    position: 'relative',
    flex: '0 0 auto',
  };

  const avatarBadgeStyle = {
    position: 'absolute',
    bottom: -7,
    right: -7,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 18,
    height: 18,
    padding: '0 6px',
    background: '#ef4444',
    color: '#ffffff',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    lineHeight: '18px',
    zIndex: 100,
    boxShadow: '0 1px 0 rgba(0,0,0,0.08)',
    whiteSpace: 'nowrap',
  };

  const MagnifierButton = ({ onClickHandler, style }) => (
    <button
      aria-label={t("Buscar")}
      onClick={onClickHandler}
      className="p-2 rounded-full hover:bg-slate-100 transition"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: NATIVE_CONTROL_H,
        width: NATIVE_CONTROL_H,
        background: 'transparent',
        border: 'none',
        color: '#111',
        ...style,
      }}
    >
      <FaSearch size={18} />
    </button>
  );

  const headerIdentityForAccess = getHeaderEffectiveIdentity(userEmail, cleanUserNameForHeader);
  const normalizedUserEmail = normalizeAccessEmail(headerIdentityForAccess.email || userEmail);
  const normalizedAccessCacheIdentity = businessAccessCacheIdentity(headerIdentityForAccess);
  const isAdminApprover = useMenuAdminAccess();
  const [businessAccessState, setBusinessAccessState] = useState(() => {
    const cached = readBusinessAccessCache(normalizedAccessCacheIdentity || normalizedUserEmail);
    return {
      loading: false,
      ownsBusiness: Boolean(cached && cached.ownsBusiness),
      workerAccess: Boolean(cached && cached.workerAccess),
      source: cached ? "cache" : "empty",
    };
  });

  useEffect(() => {
    let cancelled = false;
    const identity = getHeaderEffectiveIdentity(userEmail, cleanUserNameForHeader);
    const email = normalizeAccessEmail(identity.email || userEmail);
    const uid = identity.uid ? String(identity.uid) : "";
    const accessCacheId = businessAccessCacheIdentity(identity) || email || (uid ? `uid:${uid}` : "");

    const publish = (next, options = {}) => {
      if (cancelled) return;
      setBusinessAccessState((prev) => ({
        loading: options.loading === true,
        ownsBusiness: Boolean(next.ownsBusiness),
        workerAccess: Boolean(next.workerAccess),
        source: options.source || prev.source || "remote",
      }));
    };

    async function checkBusinessAccess() {
      if (!hasUser || (!email && !uid) || !db) {
        clearBusinessAccessCache(accessCacheId);
        publish({ ownsBusiness: false, workerAccess: false }, { source: "empty" });
        return;
      }

      if (typeof ownsBusinessOverride === 'boolean' || typeof businessAccessOverride === 'boolean') {
        const owns = typeof ownsBusinessOverride === 'boolean' ? ownsBusinessOverride : Boolean(businessAccessOverride);
        const worker = Boolean(businessAccessOverride) && !owns;
        writeBusinessAccessCache(accessCacheId, { ownsBusiness: owns, workerAccess: worker });
        publish({ ownsBusiness: owns, workerAccess: worker }, { source: "override" });
        return;
      }


      const cached = readBusinessAccessCache(accessCacheId);
      if (cached) {
        // This is what makes the header feel instant on repeat visits.
        publish(cached, { source: "cache" });
      } else {
        publish({ ownsBusiness: false, workerAccess: false }, { loading: true, source: "remote-loading" });
      }

      // Worker/owner access resolver shared with the rest of the app.
      // This catches workers added from the Workers section even when the field name varies.
      try {
        const effectiveUser = { uid: uid || null, email, displayName: identity.displayName || cleanUserNameForHeader || "" };
        const roleCached = getCachedUserBusinesses(effectiveUser);
        const cachedAccess = summarizeRoleAccess(roleCached);
        if (cachedAccess.ownsBusiness || cachedAccess.workerAccess) {
          writeBusinessAccessCache(accessCacheId, cachedAccess);
          publish(cachedAccess, { source: "role-cache-web" });
        }

        const freshRoles = await getUserBusinessesAndRoles(db, effectiveUser, {
          scanWorkers: true,
        });

        const roleAccess = summarizeRoleAccess(freshRoles);
        if (roleAccess.ownsBusiness || roleAccess.workerAccess) {
          writeBusinessAccessCache(accessCacheId, roleAccess);
          publish(roleAccess, { source: "role-service-web" });
          return;
        }
      } catch (err) {
        try { console.warn('[Header] role service access check skipped', err); } catch (_) {}
      }

      const startedAt = Date.now();
      const ownerChecks = [];
      if (email) {
        ownerChecks.push(getDocs(query(collection(db, 'businesses'), where('ownerEmail', '==', email), limit(1))));
        ownerChecks.push(getDocs(query(collection(db, 'businesses'), where('ownerEmailLower', '==', email), limit(1))));
        ownerChecks.push(getDocs(query(collection(db, 'businesses'), where('requestedByEmail', '==', email), limit(1))));
        ownerChecks.push(getDocs(query(collection(db, 'businesses'), where('businessEmail', '==', email), limit(1))));
        ownerChecks.push(getDocs(query(collection(db, 'businesses'), where('adminEmails', 'array-contains', email), limit(1))));
      }
      if (uid) {
        ownerChecks.push(getDocs(query(collection(db, 'businesses'), where('ownerUid', '==', uid), limit(1))));
        ownerChecks.push(getDocs(query(collection(db, 'businesses'), where('requestedByUid', '==', uid), limit(1))));
        ownerChecks.push(getDocs(query(collection(db, 'businesses'), where('admins', 'array-contains', uid), limit(1))));
        ownerChecks.push(getDocs(query(collection(db, 'businesses'), where('managers', 'array-contains', uid), limit(1))));
      }

      const workerChecks = [];
      if (email) {
        // Bulletproof fast path: root business arrays avoid collectionGroup/index/rules edge cases.
        workerChecks.push(getDocs(query(collection(db, 'businesses'), where('workerAccessEmails', 'array-contains', email), limit(1))));
        workerChecks.push(getDocs(query(collection(db, 'businesses'), where('workerEmails', 'array-contains', email), limit(1))));
        workerChecks.push(getDocs(query(collection(db, 'businesses'), where('allowedWorkerEmails', 'array-contains', email), limit(1))));
        [
          'email', 'emailLower', 'emailNormalized',
          'workerEmail', 'workerEmailLower', 'workerEmailNormalized',
          'employeeEmail', 'employeeEmailLower', 'employeeEmailNormalized',
          'inviteEmail', 'inviteEmailLower', 'inviteEmailNormalized',
          'contactEmail', 'contactEmailLower', 'contactEmailNormalized',
          'userEmail', 'userEmailLower', 'loginEmail', 'loginEmailLower',
        ].forEach((field) => workerChecks.push(getDocs(query(collectionGroup(db, 'employees'), where(field, '==', email), limit(1)))));
        workerChecks.push(getDocs(query(collectionGroup(db, 'employees'), where('accessEmails', 'array-contains', email), limit(1))));
        workerChecks.push(getDocs(query(collectionGroup(db, 'employees'), where('allowedEmails', 'array-contains', email), limit(1))));
      }
      if (uid) {
        workerChecks.push(getDocs(query(collection(db, 'businesses'), where('workerAccessUids', 'array-contains', uid), limit(1))));
        workerChecks.push(getDocs(query(collection(db, 'businesses'), where('workerUids', 'array-contains', uid), limit(1))));
        workerChecks.push(getDocs(query(collection(db, 'businesses'), where('allowedWorkerUids', 'array-contains', uid), limit(1))));
        [
          'userUid', 'uid', 'userId', 'authUid', 'linkedUid', 'linkedUserId', 'linkedUserUid', 'firebaseUid',
          'employeeUserUid', 'employeeUid', 'workerUid', 'workerUserUid',
        ].forEach((field) => workerChecks.push(getDocs(query(collectionGroup(db, 'employees'), where(field, '==', uid), limit(1)))));
      }

      let ownsBusiness = false;
      let workerAccess = false;
      let remaining = ownerChecks.length + workerChecks.length;

      if (remaining <= 0) {
        const finalValue = { ownsBusiness: false, workerAccess: false };
        writeBusinessAccessCache(accessCacheId, finalValue);
        publish(finalValue, { source: "remote-empty" });
        return;
      }

      const finishOne = () => {
        remaining -= 1;
        if (remaining <= 0 && !cancelled) {
          const finalValue = { ownsBusiness, workerAccess };
          writeBusinessAccessCache(accessCacheId, finalValue);
          publish(finalValue, { source: "remote" });
          try {
            if (window.__LOOP_HEADER_ACCESS_DEBUG__) {
              console.info('[Header] business access resolved', { email, ownsBusiness, workerAccess, ms: Date.now() - startedAt });
            }
          } catch (_) {}
        }
      };

      const observe = (promise, type) => {
        promise
          .then((snap) => {
            const found = Boolean(snap && !snap.empty);
            if (!found || cancelled) return;
            if (type === 'owner') ownsBusiness = true;
            if (type === 'worker') workerAccess = true;

            const currentValue = { ownsBusiness, workerAccess };
            writeBusinessAccessCache(accessCacheId, currentValue);
            // Update immediately as soon as any positive query returns; do not wait for all 7 checks.
            publish(currentValue, { source: "remote-fast" });
          })
          .catch((err) => {
            try { console.warn('[Header] business access check failed', err); } catch (_) {}
          })
          .finally(finishOne);
      };

      ownerChecks.forEach((promise) => observe(promise, 'owner'));
      workerChecks.forEach((promise) => observe(promise, 'worker'));
    }

    checkBusinessAccess();
    return () => { cancelled = true; };
  }, [db, hasUser, userEmail, cleanUserNameForHeader, isAdminApprover, businessAccessOverride, ownsBusinessOverride]);

  const ownsBusinessForUi = Boolean(businessAccessState.ownsBusiness || ownsBusinessOverride === true);

  // Parent-gated calendar access:
  // MainMenu already resolves owner/worker/manual-auth access and only passes onOpenCalendar
  // when the calendar should be reachable. Trusting this prop makes the icon instant
  // and prevents iOS workers from losing the button while Header's own background
  // access refresh is still loading.
  const canOpenCalendarFromParent = typeof onOpenCalendar === 'function';
  const canUseCalendarForUi = Boolean(
    isAdminApprover ||
    businessAccessState.ownsBusiness ||
    businessAccessState.workerAccess ||
    businessAccessOverride === true ||
    (hasUser && canOpenCalendarFromParent)
  );

  const currentPath = location && location.pathname ? location.pathname : '/';
  const currentSearch = location && location.search ? location.search : "";

  const routeBusinessContext = useMemo(() => {
    const path = String(currentPath || "");
    const search = String(currentSearch || "");
    const isBusinessContextRoute = /\/(admin-calendar|business-edit|business-settings|owner-business|business-admin)(?:\/|$)/i.test(path);
    if (!isBusinessContextRoute) return { businessId: "", businessName: "" };

    let fromSearch = "";
    try {
      const params = new URLSearchParams(search || "");
      fromSearch = params.get("businessId") || params.get("business") || "";
    } catch (_) {}

    let fromPath = "";
    try {
      const match = path.match(/\/(?:admin-calendar|business-edit|business-settings|owner-business|business-admin)\/([^/?#]+)/i);
      fromPath = match && match[1] ? decodeURIComponent(match[1]) : "";
    } catch (_) {}

    let fromStorage = "";
    let nameFromStorage = "";
    try {
      fromStorage = window.localStorage?.getItem?.("loopActiveBusinessId") || window.localStorage?.getItem?.("loopAgendaActiveBusinessId") || "";
      nameFromStorage = window.localStorage?.getItem?.("loopActiveBusinessName") || "";
    } catch (_) {}

    const businessId = String(activeBusinessIdProp || fromSearch || fromPath || fromStorage || "").trim();
    const businessName = String(activeBusinessNameProp || nameFromStorage || "").trim();
    return { businessId, businessName };
  }, [currentPath, currentSearch, activeBusinessIdProp, activeBusinessNameProp]);

  const [notificationBusinessContext, setNotificationBusinessContext] = useState(routeBusinessContext);

  useEffect(() => {
    setNotificationBusinessContext(routeBusinessContext);
  }, [routeBusinessContext.businessId, routeBusinessContext.businessName]);

  useEffect(() => {
    const handler = (event) => {
      try {
        const detail = event?.detail || {};
        const path = String((window.location && window.location.pathname) || currentPath || "");
        const isBusinessContextRoute = /\/(admin-calendar|business-edit|business-settings|owner-business|business-admin)(?:\/|$)/i.test(path);
        if (!isBusinessContextRoute) return;
        const businessId = String(detail.businessId || detail.id || "").trim();
        if (!businessId) return;
        setNotificationBusinessContext({
          businessId,
          businessName: String(detail.businessName || detail.name || "").trim(),
        });
      } catch (_) {}
    };
    window.addEventListener("loop:active-business-context", handler);
    return () => window.removeEventListener("loop:active-business-context", handler);
  }, [currentPath]);

  const isAdminCalendarRoute = /\/admin-calendar(?:\/|$)/i.test(currentPath || '');
  const isAuthPage =
    currentPath === '/login' ||
    currentPath === '/register' ||
    currentPath === '/signup' ||
    currentPath === '/sign-up' ||
    currentPath === '/forgot-password' ||
    currentPath.includes('/auth');

  const handleSelectBusinessFromSuggestions = (biz) => {
    try {
      if (!biz) return;
      rememberLoopSearch(biz?.name || biz?.businessName || biz?.title || '');
      if (biz && (biz.id || biz.slug)) navigate(businessPublicPath(biz));
      if (typeof setShowSuggestions === 'function') setShowSuggestions(false);
      setShowMobileSearch(false);
    } catch (err) {
      console.warn('onSelectBusiness handler failed', err);
    }
  };

  const handleSelectServiceFromSuggestions = (serviceKey) => {
    try {
      if (!serviceKey) return;
      const raw = typeof serviceKey === 'object'
        ? (serviceKey.name || serviceKey.label || serviceKey.title || serviceKey.value || serviceKey.serviceKey || serviceKey.id || '')
        : serviceKey;
      const clean = String(raw || '').trim();
      if (!clean) return;
      rememberLoopSearch(clean);
      const normalized = clean.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      if (normalized.includes('evento')) {
        navigate('/events');
      } else if (typeof onSearch === 'function') {
        onSearch(clean);
      } else {
        navigate(`/services/search?q=${encodeURIComponent(clean)}`);
      }
      if (typeof setShowSuggestions === 'function') setShowSuggestions(false);
      setShowMobileSearch(false);
    } catch (err) {
      console.warn('onSelectService handler failed', err);
    }
  };

  const handleSelectLocationFromSuggestions = (suggestion) => {
    try {
      if (!suggestion) return;
      const label = String(suggestion.label || '').trim();
      if (label) {
        setEffectiveQ(label);
        rememberLoopSearch(label);
      }
      if (typeof onSearchNearby === 'function') onSearchNearby(suggestion);
      if (label) navigate(`/location-results?loc=${encodeURIComponent(label)}`);
      if (typeof setShowSuggestions === 'function') setShowSuggestions(false);
      setShowMobileSearch(false);
    } catch (err) {
      console.warn('onSelectLocation handler failed', err);
    }
  };

  useEffect(() => {
    if (!showSuggestions && !showMobileSearch) return undefined;

    const isInsideSearchSurface = (target) => {
      try {
        return Boolean(
          (desktopSearchRef.current && desktopSearchRef.current.contains(target)) ||
          (mobileSearchRef.current && mobileSearchRef.current.contains(target)) ||
          (target && target.closest && target.closest('.loop-search-suggestions-portal'))
        );
      } catch (_) {
        return false;
      }
    };

    const handlePointerAway = (event) => {
      if (!event || !event.target) return;
      if (isInsideSearchSurface(event.target)) return;
      closeSearchUI({ closeMobile: false });
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') closeSearchUI({ closeMobile: true });
    };

    document.addEventListener('mousedown', handlePointerAway, true);
    document.addEventListener('touchstart', handlePointerAway, true);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerAway, true);
      document.removeEventListener('touchstart', handlePointerAway, true);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showSuggestions, showMobileSearch, setShowSuggestions]);

  useEffect(() => {
    closeSearchUI({ closeMobile: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  useEffect(() => {
    const openSearch = () => {
      setShowMobileSearch(true);
      if (typeof setShowSuggestions === 'function') {
        setShowSuggestions(Boolean(effectiveQ && String(effectiveQ).trim()));
      }
    };

    const openProfile = () => {
      if (!hasUser) {
        handleLoginClick();
        return;
      }

    
  const closeMobileProfileSheet = () => {
    setShowMobileProfileSheet(false);
  };

  const runMobileProfileAction = (fn, fallbackPath = null) => {
    closeMobileProfileSheet();

    try {
      if (typeof fn === 'function') {
        fn();
        return;
      }

      if (fallbackPath) {
        navigate(fallbackPath);
      }
    } catch (err) {
      console.warn('Mobile profile action failed', err);
      if (fallbackPath) {
        try { navigate(fallbackPath); } catch (_) {}
      }
    }
  };

  const mobileProfileRows = [
    {
      key: 'profile',
      label: 'Mi perfil',
      description: 'Cuenta, datos personales y configuración',
      icon: 'profile',
      onClick: () => runMobileProfileAction(onShowUserProfile, '/profile'),
    },
    {
      key: 'bookings',
      label: t("Mis reservas"),
      description: 'Consulta tus reservas activas e historial',
      icon: 'bookings',
      onClick: () => runMobileProfileAction(onShowMyBookings, '/my-bookings'),
    },
    {
      key: 'loyalty',
      label: 'Mis beneficios',
      description: 'Sellos y recompensas de tus negocios',
      icon: 'rating',
      onClick: () => runMobileProfileAction(onShowMyLoyalty, '/my-benefits'),
    },
    {
      key: 'business',
      label: 'Crear negocio',
      description: 'Publica tus servicios y recibe reservas',
      icon: 'business',
      onClick: () => runMobileProfileAction(onCreateBusiness, '/new-business'),
    },
  ].filter(Boolean);

  if (ownsBusinessForUi && typeof onAdminManage === 'function') {
    mobileProfileRows.splice(2, 0, {
      key: 'admin',
      label: 'Administrar negocios',
      description: 'Gestiona tus negocios, servicios y equipo',
      icon: 'admin',
      onClick: () => runMobileProfileAction(onAdminManage, '/owner-businesses'),
    });
  }

  if (isAdminApprover) {
    mobileProfileRows.splice(2, 0, {
      key: 'owner-console',
      label: 'Loop Admin',
      description: 'Owner console, aprobaciones, analytics y finanzas',
      icon: 'admin',
      onClick: () => runMobileProfileAction(null, '/owner-console'),
    });
  }

  if (typeof onShowPending === 'function') {
    mobileProfileRows.splice(2, 0, {
      key: 'pending',
      label: 'Pendientes',
      description: 'Revisa solicitudes y acciones pendientes',
      icon: 'pending',
      onClick: () => runMobileProfileAction(onShowPending, '/pendings'),
    });
  }

  const MobileProfileIcon = ({ type }) => {
    const common = {
      width: 20,
      height: 20,
      viewBox: '0 0 24 24',
      fill: 'none',
      stroke: 'currentColor',
      strokeWidth: 2.05,
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
      'aria-hidden': true,
    };

    if (type === 'bookings') {
      return <svg {...common}><path d="M6.5 4.5A2.5 2.5 0 0 1 9 2h6a2.5 2.5 0 0 1 2.5 2.5V21L12 17.8 6.5 21V4.5Z" /></svg>;
    }

    if (type === 'business') {
      return <svg {...common}><path d="M3 21h18" /><path d="M5 21V7l7-4 7 4v14" /><path d="M9 21v-6h6v6" /></svg>;
    }

    if (type === 'admin') {
      return <svg {...common}><path d="M12 3l8 4v6c0 5-3.5 7.5-8 8-4.5-.5-8-3-8-8V7l8-4Z" /><path d="M9 12l2 2 4-4" /></svg>;
    }

    if (type === 'pending') {
      return <svg {...common}><path d="M12 8v5l3 2" /><circle cx="12" cy="12" r="9" /></svg>;
    }

    if (type === 'logout') {
      return <svg {...common}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="M16 17l5-5-5-5" /><path d="M21 12H9" /></svg>;
    }

    return <svg {...common}><path d="M20 21a8 8 0 1 0-16 0" /><circle cx="12" cy="7" r="4" /></svg>;
  };


  if (useMobileLayout) {
        setShowMobileProfileSheet(true);
        return;
      }

      if (typeof setOpenAvatar === 'function') setOpenAvatar(true);
    };

    try {
      window.addEventListener('loop-open-mobile-search', openSearch);
      window.addEventListener('loop-open-avatar-dropdown', openProfile);
    } catch (_) {}

    return () => {
      try {
        window.removeEventListener('loop-open-mobile-search', openSearch);
        window.removeEventListener('loop-open-avatar-dropdown', openProfile);
      } catch (_) {}
    };
  }, [effectiveQ, hasUser, setOpenAvatar, setShowSuggestions]);

  if (!useMobileLayout) {
    return (
      <header
        id="app-main-header"
        className={`loop-ref-header ${isAdminCalendarRoute ? 'loop-header-admin-context' : ''}`}
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 'var(--loop-z-header, 1000)',
          width: '100%',
          minHeight: 78,
          backgroundColor: '#00675f',
          backgroundImage: 'linear-gradient(135deg, #005c55 0%, #006f66 50%, #00534d 100%)',
          borderBottom: '0',
          boxShadow: '0 16px 36px rgba(0, 76, 70, 0.28)',
          paddingTop: 'env(safe-area-inset-top)',
          overflow: 'visible',
        }}
      >
        <style>{headerAirbnbRefStyles}</style>

        <div className="loop-ref-header-inner">
          <div>
            <button
              type="button"
              className="loop-ref-logo-button"
              onClick={handleLogoClick}
              aria-label={t("Inicio")}
            >
              <div
                data-loop-logo
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  overflow: 'hidden',
                  height: 62,
                  maxWidth: 178,
                }}
              >
                <LoopLogo alt="Loop" height={62} width={178} />
              </div>
            </button>
          </div>

          <div className={`loop-ref-search-wrap ${currentPath.includes("calendar") ? "loop-workspace-search" : ""}`}>
            <form ref={desktopSearchRef} onSubmit={handleSubmit} style={{ position: 'relative', minWidth: 0 }}>
              <span className="loop-ref-icon-left">
                <FaSearch size={17} />
              </span>

              <input
                id="header-search-input"
                value={effectiveQ}
                onChange={(e) => {
                  setEffectiveQ(e.target.value);
                  if (typeof setShowSuggestions === 'function') {
                    setShowSuggestions(true);
                  }
                }}
                onFocus={() => {
                  if (typeof setShowSuggestions === 'function') {
                    setShowSuggestions(true);
                  }
                }}
                placeholder={t("Busca un servicio o negocio")}
                className="loop-ref-search-input"
                aria-label={t("Busca un servicio o negocio")}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="none"
                spellCheck={false}
                name="loop-global-search"
                inputMode="search"
              />

              {effectiveQ ? (
                <button
                  type="button"
                  onClick={() => closeSearchUI({ clearQuery: true, closeMobile: false })}
                  aria-label={t("Limpiar búsqueda")}
                  style={{
                    position: 'absolute',
                    right: 12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 30,
                    height: 30,
                    borderRadius: 999,
                    border: '1px solid rgba(226,232,240,.9)',
                    background: '#ffffff',
                    color: '#64748b',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    zIndex: 3,
                  }}
                >
                  <FaTimes size={12} />
                </button>
              ) : null}

              {showSuggestions && !showMobileSearch && desktopSearchPortalRect && typeof document !== 'undefined' ? createPortal((
                <div
                  className="loop-search-suggestions-layer loop-search-suggestions-portal"
                  style={{
                    position: 'fixed',
                    '--loop-search-portal-top': `${desktopSearchPortalRect.top}px`,
                    '--loop-search-portal-left': `${desktopSearchPortalRect.left}px`,
                    '--loop-search-portal-width': `${desktopSearchPortalRect.width}px`,
                    zIndex: 'var(--loop-z-header-panel, 1200)',
                  }}
                >
                  <SearchSuggestions
                    onlyPublicBusinesses={true}
                    q={effectiveQ}
                    db={db}
                    locations={locationSuggestions}
                    locationLoading={locationLoading}
                    onSelectBusiness={handleSelectBusinessFromSuggestions}
                    onSelectService={handleSelectServiceFromSuggestions}
                    onSelectLocation={handleSelectLocationFromSuggestions}
                  />
                </div>
              ), document.body) : null}
            </form>
          </div>

          <div className="loop-ref-actions"><MarketPicker />
            {!ownsBusinessForUi && !canUseCalendarForUi && !authHydrating && !businessAccessState.loading ? <button type="button" className="loop-ref-nav-btn loop-publish-business" onClick={() => typeof onCreateBusiness === 'function' ? onCreateBusiness() : navigate('/new-business')}>{t("Publicar mi negocio")}</button> : null}
            {hasUser ? (
              <>
                {canUseCalendarForUi || ownsBusinessForUi ? (
                  <button type="button" onClick={handleCalendarClick} aria-label={t("Mi negocio")} className="loop-ref-nav-btn loop-business-entry">
                    <BusinessIconSvg /><span>{t("Mi negocio")}</span>
                    {calendarPendingCount > 0 ? <span className="loop-ref-badge" style={{position:'static'}}>{calendarPendingCount}</span> : null}
                  </button>
                ) : (
                  <button type="button" onClick={handleMyBookingsClick} aria-label={t("Mis reservas")} className="loop-ref-nav-btn loop-customer-entry">
                    <BookmarkIconSvg /><span>{t("Mis reservas")}</span>
                  </button>
                )}

                <NotificationCenter
                  db={db}
                  userEmail={userEmail}
                  fallbackUnreadCount={pendingCount}
                  activeBusinessId={notificationBusinessContext.businessId}
                  activeBusinessName={notificationBusinessContext.businessName}
                  buttonClassName="loop-ref-bell"
                  badgeClassName="loop-ref-badge"
                  icon={<BellIconSvg />}
                />
              </>
            ) : authHydrating ? (
              <span
                aria-hidden="true"
                className="loop-ref-nav-btn"
                style={{ visibility: "hidden", pointerEvents: "none" }}
              >
                <span>{t("Iniciar sesión")}</span>
              </span>
            ) : (
              <button type="button" onClick={handleLoginClick} title={t("Conectarse")} className="loop-ref-nav-btn loop-ref-login-btn">
                <span>{t("Iniciar sesión")}</span>
              </button>
            )}

            {hasUser ? (
              <div style={{ position: 'relative', zIndex: 80, display: 'inline-block', overflow: 'visible' }}>
                <button
                  ref={avatarTriggerRef}
                  type="button"
                  onClick={() => setOpenAvatar && setOpenAvatar(true)}
                  aria-label={t("Abrir menú de cuenta")}
                  aria-haspopup="menu"
                  aria-expanded={Boolean(openAvatar)}
                  className="loop-ref-avatar"
                >
                  <span style={{ userSelect: 'none', fontSize: 16 }}>{String(userInitial).toUpperCase()}</span>
                </button>
              </div>
            ) : null}
          </div>
        </div>

        <div style={{ position: 'relative', zIndex: 200 }}>
          {hasUser && (
            <AvatarDropdown
              userName={userName}
              userEmail={userEmail}
              open={openAvatar}
              setOpen={setOpenAvatar}
              hideTrigger={true}
              externalTriggerRef={avatarTriggerRef}
              onShowUserProfile={onShowUserProfile}
              onAdminManage={onAdminManage}
              onShowMyBookings={onShowMyBookings}
              onShowMyLoyalty={handleMyLoyaltyClick}
              onShowPending={onShowPending}
              onCreateBusiness={onCreateBusiness}
              onLogout={onLogout}
              showBusinessAdmin={ownsBusinessForUi}
              onPlatformAdmin={isAdminApprover ? () => navigate("/owner-console") : null}
            />
          )}
        </div>
      </header>
    );
  }

  if (useMobileLayout) {
    if (isAdminCalendarRoute) {
      return null;
    }

    const safeTop = 'env(safe-area-inset-top, 0px)';
    const mobileHeaderHeight = `calc(${safeTop} + 58px)`;
    // Phase 1M: the mobile header now participates in normal layout using sticky.
    // The old fixed-header spacer created a large white gap between the header and content.
    const spacerHeight = '0px';

    const mobileHeaderStyles = `
      :root {
        --loop-safe-top: env(safe-area-inset-top, 0px);
        --loop-safe-bottom: max(env(safe-area-inset-bottom, 0px), 34px);
        --loop-mobile-header-height: 58px;
        --loop-mobile-header-total: calc(var(--loop-safe-top) + var(--loop-mobile-header-height));
        --loop-bottom-action-offset: calc(var(--loop-safe-bottom) + 12px);
      }

      html, body, #root {
        min-height: 100%;
      }


      /* Phase 1M: no global mobile bottom navigation. Header is the single navigation surface. */
      :root {
        --loop-mobile-nav-height: 0px !important;
      }


      /* HARD LOCK: mobile header must never switch to white/cream at any breakpoint. */
      #app-main-header.loop-mobile-header-v2,
      body #app-main-header.loop-mobile-header-v2,
      html body #root #app-main-header.loop-mobile-header-v2 {
        background-color: #134f47 !important;
        background-image:
          radial-gradient(circle at 10% 0%, rgba(255,248,238,.12), transparent 28%),
          linear-gradient(135deg, #103f39 0%, #14554d 56%, #1d6b61 100%) !important;
        background:
          radial-gradient(circle at 10% 0%, rgba(255,248,238,.12), transparent 28%),
          linear-gradient(135deg, #103f39 0%, #14554d 56%, #1d6b61 100%) !important;
        border-bottom: 0 !important;
        box-shadow: 0 12px 30px rgba(16,63,57,.20) !important;
      }

      #app-main-header.loop-mobile-header-v2 .loop-mobile-header-row {
        background: transparent !important;
      }

      .loop-mobile-header-v2 {
        position: sticky;
        left: 0;
        right: 0;
        top: 0;
        z-index: var(--loop-z-header, 1000);
        min-height: var(--loop-mobile-header-total);
        padding-top: var(--loop-safe-top);
        box-sizing: border-box;
        background:
          radial-gradient(circle at 10% 0%, rgba(255,250,242,.14), transparent 34%),
          linear-gradient(135deg, #004f47 0%, #00675f 56%, #00796f 100%);
        border-bottom: 0 !important;
        box-shadow: 0 12px 30px rgba(0, 76, 70, .20);
        backdrop-filter: blur(18px) saturate(1.06);
        -webkit-backdrop-filter: blur(18px) saturate(1.06);
      }

      .loop-mobile-header-row {
        height: var(--loop-mobile-header-height);
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto;
        align-items: center;
        gap: 10px;
        padding: 0 12px;
        box-sizing: border-box;
      }

      .loop-mobile-logo-btn {
        border: 0;
        background: transparent;
        padding: 0;
        min-width: 0;
        height: 44px;
        display: inline-flex;
        align-items: center;
        justify-content: flex-start;
        overflow: hidden;
        cursor: pointer;
      }

      .loop-mobile-logo-btn img {
        max-height: 36px !important;
        max-width: 116px !important;
        object-fit: contain !important;
      }

      .loop-mobile-actions-v2 {
        display: inline-flex;
        align-items: center;
        justify-content: flex-end;
        gap: 7px;
        min-width: 0;
      }

      .loop-mobile-icon-btn-v2 {
        width: 40px;
        height: 40px;
        min-width: 40px;
        border-radius: 999px;
        border: 1px solid rgba(255,255,255,.18);
        background: rgba(255,250,242,.13);
        color: #fffaf2;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        box-shadow: none;
        cursor: pointer;
        position: relative;
        -webkit-tap-highlight-color: transparent;
      }

      .loop-mobile-icon-btn-v2.primary {
        background: rgba(255,250,242,.96);
        color: #00675f;
        border-color: rgba(255,250,242,.66);
        box-shadow: 0 8px 18px rgba(15,23,42,.12);
      }

      .loop-mobile-icon-btn-v2 svg {
        width: 19px;
        height: 19px;
        stroke-width: 2.25;
      }

      .loop-mobile-badge-v2 {
        position: absolute;
        right: -4px;
        top: -5px;
        min-width: 18px;
        height: 18px;
        padding: 0 5px;
        border-radius: 999px;
        background: #fffaf2;
        color: #00675f;
        border: 2px solid #00675f;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 10px;
        font-weight: 950;
        line-height: 1;
        box-sizing: border-box;
      }

      /* LOOP MOBILE HEADER SEAM GAP FIX HOTFIX */
      .loop-mobile-spacer-v2 {
        height: 0 !important;
        min-height: 0 !important;
        max-height: 0 !important;
        flex: 0 0 0 !important;
        display: none !important;
        margin: 0 !important;
        padding: 0 !important;
        overflow: hidden !important;
      }

      .loop-mobile-avatar-dropdown-mount {
        height: 0 !important;
        min-height: 0 !important;
        max-height: 0 !important;
        margin: 0 !important;
        padding: 0 !important;
        display: block !important;
        position: relative !important;
        z-index: var(--loop-z-header-panel, 1200) !important;
        overflow: visible !important;
        background: transparent !important;
      }

      .loop-mobile-avatar-dropdown-mount > * {
        pointer-events: auto !important;
      }

      .mainmenu-header-only-root > #app-main-header.loop-mobile-header-v2 + .loop-mobile-avatar-dropdown-mount {
        height: 0 !important;
        min-height: 0 !important;
        max-height: 0 !important;
      }

      .loop-mobile-search-sheet-v2 {
        position: fixed;
        inset: 0;
        z-index: var(--loop-z-header-panel, 1200);
        background: rgba(255, 250, 242, .98);
        backdrop-filter: blur(16px);
        -webkit-backdrop-filter: blur(16px);
        padding-top: var(--loop-safe-top);
        box-sizing: border-box;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
      }

      .loop-mobile-search-top-v2 {
        display: grid;
        grid-template-columns: minmax(0, 1fr) 44px;
        gap: 10px;
        align-items: center;
        padding: 12px 12px 10px;
        box-sizing: border-box;
      }

      .loop-mobile-search-field-v2 {
        position: relative;
        min-width: 0;
      }

      .loop-mobile-search-field-v2 input {
        width: 100%;
        height: 48px;
        border-radius: 18px;
        border: 1px solid rgba(226,232,240,.96);
        background: #ffffff;
        color: #0f172a;
        outline: none;
        padding: 0 14px 0 44px;
        box-sizing: border-box;
        font-size: 14px;
        font-weight: 760;
        box-shadow: 0 12px 26px rgba(15,23,42,.08);
      }

      .loop-mobile-search-field-v2 span {
        position: absolute;
        left: 16px;
        top: 50%;
        transform: translateY(-50%);
        color: #475569;
        display: inline-flex;
      }

      .loop-mobile-search-close-v2 {
        width: 44px;
        height: 44px;
        border-radius: 999px;
        border: 1px solid #e2e8f0;
        background: #ffffff;
        color: #475569;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }

      .loop-mobile-search-results-v2 {
        width: 100%;
        max-width: 640px;
        margin: 0 auto;
        padding: 0 12px calc(var(--loop-safe-bottom) + 24px);
        box-sizing: border-box;
      }

      .loop-mobile-login-sheet-v2 {
        position: fixed;
        inset: 0;
        z-index: var(--loop-z-header-panel, 1200);
        pointer-events: auto;
      }

      .loop-mobile-login-panel-v2 {
        position: absolute;
        left: 12px;
        right: 12px;
        bottom: var(--loop-bottom-action-offset);
        border-radius: 28px;
        background: #ffffff;
        border: 1px solid rgba(226,232,240,.96);
        box-shadow: 0 28px 90px rgba(15,23,42,.30);
        overflow: hidden;
      }

      @media (max-width: 380px) {
        .loop-mobile-header-row { padding-left: 10px; padding-right: 10px; gap: 8px; }
        .loop-mobile-actions-v2 { gap: 5px; }
        .loop-mobile-icon-btn-v2 { width: 38px; height: 38px; min-width: 38px; }
        .loop-mobile-logo-btn img { max-width: 102px !important; }
      }


      /* Phase 1Q — final mobile header alignment.
         Single emerald row, centered controls, no visual bleed into content. */
      @media (max-width: 920px) {
        #app-main-header.loop-mobile-header-v2 {
          margin: 0 !important;
          min-height: calc(env(safe-area-inset-top, 0px) + 64px) !important;
          height: calc(env(safe-area-inset-top, 0px) + 64px) !important;
          padding-top: env(safe-area-inset-top, 0px) !important;
          padding-bottom: 0 !important;
          display: block !important;
          overflow: visible !important;
        }

        #app-main-header.loop-mobile-header-v2 .loop-mobile-header-row {
          height: 64px !important;
          min-height: 64px !important;
          max-height: 64px !important;
          padding: 0 12px !important;
          display: grid !important;
          grid-template-columns: minmax(92px, 1fr) auto !important;
          align-items: center !important;
          justify-items: stretch !important;
          gap: 8px !important;
          margin: 0 !important;
          box-sizing: border-box !important;
          background: transparent !important;
        }

        #app-main-header.loop-mobile-header-v2 .loop-mobile-logo-btn {
          height: 44px !important;
          min-height: 44px !important;
          max-height: 44px !important;
          align-self: center !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: flex-start !important;
          margin: 0 !important;
          padding: 0 !important;
          transform: none !important;
        }

        #app-main-header.loop-mobile-header-v2 .loop-mobile-logo-btn img {
          max-height: 34px !important;
          max-width: 112px !important;
          display: block !important;
        }

        #app-main-header.loop-mobile-header-v2 .loop-mobile-actions-v2 {
          height: 44px !important;
          min-height: 44px !important;
          max-height: 44px !important;
          align-self: center !important;
          align-items: center !important;
          justify-content: flex-end !important;
          gap: 6px !important;
          margin: 0 !important;
          padding: 0 !important;
          transform: none !important;
          overflow: visible !important;
        }

        #app-main-header.loop-mobile-header-v2 .loop-mobile-icon-btn-v2 {
          width: 40px !important;
          height: 40px !important;
          min-width: 40px !important;
          min-height: 40px !important;
          max-width: 40px !important;
          max-height: 40px !important;
          align-self: center !important;
          margin: 0 !important;
          transform: none !important;
        }

        #app-main-header.loop-mobile-header-v2 + .loop-mobile-spacer-v2 {
          display: none !important;
          height: 0 !important;
          min-height: 0 !important;
          margin: 0 !important;
          padding: 0 !important;
        }
      }

      @media (max-width: 380px) {
        #app-main-header.loop-mobile-header-v2 .loop-mobile-header-row {
          padding-left: 10px !important;
          padding-right: 10px !important;
          gap: 6px !important;
        }
        #app-main-header.loop-mobile-header-v2 .loop-mobile-actions-v2 { gap: 5px !important; }
        #app-main-header.loop-mobile-header-v2 .loop-mobile-icon-btn-v2 {
          width: 38px !important;
          height: 38px !important;
          min-width: 38px !important;
          min-height: 38px !important;
          max-width: 38px !important;
          max-height: 38px !important;
        }
        #app-main-header.loop-mobile-header-v2 .loop-mobile-logo-btn img { max-width: 102px !important; }
      }


      /* Phase 1R — right-fit mobile actions.
         Keep the Loop logo compact on the left and push every action fully to the right.
         Widths step down by viewport so the row fits on smaller Android/iPhone screens. */
      @media (max-width: 920px) {
        #app-main-header.loop-mobile-header-v2 .loop-mobile-header-row {
          display: flex !important;
          align-items: center !important;
          justify-content: flex-start !important;
          width: 100% !important;
          padding-left: clamp(10px, 2.8vw, 14px) !important;
          padding-right: clamp(10px, 2.8vw, 14px) !important;
          gap: 8px !important;
        }

        #app-main-header.loop-mobile-header-v2 .loop-mobile-logo-btn {
          flex: 0 1 auto !important;
          width: auto !important;
          max-width: clamp(86px, 25vw, 118px) !important;
          min-width: 0 !important;
          margin-right: 0 !important;
        }

        #app-main-header.loop-mobile-header-v2 .loop-mobile-logo-btn img {
          width: auto !important;
          max-width: clamp(86px, 25vw, 112px) !important;
          max-height: 34px !important;
        }

        #app-main-header.loop-mobile-header-v2 .loop-mobile-actions-v2 {
          margin-left: auto !important;
          margin-right: 0 !important;
          flex: 0 0 auto !important;
          width: auto !important;
          max-width: calc(100vw - clamp(96px, 28vw, 132px)) !important;
          display: inline-flex !important;
          justify-content: flex-end !important;
          align-items: center !important;
          gap: clamp(4px, 1.6vw, 7px) !important;
          overflow: visible !important;
        }
      }

      @media (max-width: 430px) {
        #app-main-header.loop-mobile-header-v2 .loop-mobile-header-row {
          padding-left: 10px !important;
          padding-right: 10px !important;
          gap: 6px !important;
        }

        #app-main-header.loop-mobile-header-v2 .loop-mobile-logo-btn img {
          max-width: 96px !important;
          max-height: 32px !important;
        }

        #app-main-header.loop-mobile-header-v2 .loop-mobile-actions-v2 {
          gap: 4px !important;
          max-width: calc(100vw - 116px) !important;
        }

        #app-main-header.loop-mobile-header-v2 .loop-mobile-icon-btn-v2 {
          width: 36px !important;
          height: 36px !important;
          min-width: 36px !important;
          min-height: 36px !important;
          max-width: 36px !important;
          max-height: 36px !important;
        }

        #app-main-header.loop-mobile-header-v2 .loop-mobile-icon-btn-v2 svg {
          width: 17px !important;
          height: 17px !important;
        }
      }

      @media (max-width: 360px) {
        #app-main-header.loop-mobile-header-v2 .loop-mobile-header-row {
          padding-left: 8px !important;
          padding-right: 8px !important;
          gap: 5px !important;
        }

        #app-main-header.loop-mobile-header-v2 .loop-mobile-logo-btn img {
          max-width: 82px !important;
          max-height: 30px !important;
        }

        #app-main-header.loop-mobile-header-v2 .loop-mobile-actions-v2 {
          gap: 3px !important;
          max-width: calc(100vw - 98px) !important;
        }

        #app-main-header.loop-mobile-header-v2 .loop-mobile-icon-btn-v2 {
          width: 32px !important;
          height: 32px !important;
          min-width: 32px !important;
          min-height: 32px !important;
          max-width: 32px !important;
          max-height: 32px !important;
        }

        #app-main-header.loop-mobile-header-v2 .loop-mobile-icon-btn-v2 svg {
          width: 15px !important;
          height: 15px !important;
        }

        #app-main-header.loop-mobile-header-v2 .loop-mobile-badge-v2 {
          right: -5px !important;
          top: -6px !important;
          min-width: 16px !important;
          height: 16px !important;
          font-size: 9px !important;
          border-width: 1.5px !important;
        }
      }


      /* =========================================================
         PHASE 1S — MOBILE/TABLET HEADER EDGE LOCK
         - Forces the mobile header to use the full viewport width.
         - Pins action buttons to the real right edge.
         - Keeps buttons vertically centered inside the emerald bar.
         - Prevents visual bleed below the header line.
         ========================================================= */
      @media (max-width: 920px) {
        #app-main-header.loop-mobile-header-v2 {
          width: 100vw !important;
          max-width: 100vw !important;
          left: 0 !important;
          right: auto !important;
          margin: 0 !important;
          min-height: calc(env(safe-area-inset-top, 0px) + 58px) !important;
          height: calc(env(safe-area-inset-top, 0px) + 58px) !important;
          padding-top: env(safe-area-inset-top, 0px) !important;
          padding-left: 0 !important;
          padding-right: 0 !important;
          padding-bottom: 0 !important;
          overflow: hidden !important;
          box-sizing: border-box !important;
        }

        #app-main-header.loop-mobile-header-v2 .loop-mobile-header-row {
          width: 100vw !important;
          max-width: 100vw !important;
          height: 58px !important;
          min-height: 58px !important;
          max-height: 58px !important;
          padding-left: max(10px, env(safe-area-inset-left, 0px)) !important;
          padding-right: max(8px, env(safe-area-inset-right, 0px)) !important;
          margin: 0 !important;
          box-sizing: border-box !important;
          display: grid !important;
          grid-template-columns: minmax(76px, auto) minmax(0, 1fr) auto !important;
          grid-template-areas: "logo spacer actions" !important;
          align-items: center !important;
          gap: 0 !important;
          overflow: hidden !important;
          background: transparent !important;
        }

        #app-main-header.loop-mobile-header-v2 .loop-mobile-logo-btn {
          grid-area: logo !important;
          justify-self: start !important;
          align-self: center !important;
          width: auto !important;
          max-width: clamp(78px, 23vw, 108px) !important;
          height: 42px !important;
          min-height: 42px !important;
          max-height: 42px !important;
          margin: 0 !important;
          padding: 0 !important;
          overflow: hidden !important;
          flex: 0 1 auto !important;
        }

        #app-main-header.loop-mobile-header-v2 .loop-mobile-logo-btn img {
          width: auto !important;
          max-width: clamp(78px, 23vw, 108px) !important;
          max-height: 32px !important;
          display: block !important;
          object-fit: contain !important;
        }

        #app-main-header.loop-mobile-header-v2 .loop-mobile-actions-v2 {
          grid-area: actions !important;
          justify-self: end !important;
          align-self: center !important;
          margin-left: 0 !important;
          margin-right: 0 !important;
          padding: 0 !important;
          height: 42px !important;
          min-height: 42px !important;
          max-height: 42px !important;
          max-width: calc(100vw - clamp(92px, 28vw, 128px)) !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: flex-end !important;
          gap: 5px !important;
          overflow: visible !important;
          transform: none !important;
          flex: 0 0 auto !important;
        }

        #app-main-header.loop-mobile-header-v2 .loop-mobile-icon-btn-v2 {
          width: 38px !important;
          height: 38px !important;
          min-width: 38px !important;
          min-height: 38px !important;
          max-width: 38px !important;
          max-height: 38px !important;
          flex: 0 0 38px !important;
          align-self: center !important;
          margin: 0 !important;
          padding: 0 !important;
          transform: none !important;
          box-sizing: border-box !important;
        }

        #app-main-header.loop-mobile-header-v2 .loop-mobile-icon-btn-v2 svg {
          width: 17px !important;
          height: 17px !important;
        }

        #app-main-header.loop-mobile-header-v2 .loop-mobile-badge-v2 {
          right: -2px !important;
          top: -3px !important;
          min-width: 16px !important;
          height: 16px !important;
          border-width: 1.5px !important;
          font-size: 9px !important;
        }
      }

      @media (max-width: 430px) {
        #app-main-header.loop-mobile-header-v2 .loop-mobile-header-row {
          padding-left: max(9px, env(safe-area-inset-left, 0px)) !important;
          padding-right: max(7px, env(safe-area-inset-right, 0px)) !important;
        }

        #app-main-header.loop-mobile-header-v2 .loop-mobile-logo-btn,
        #app-main-header.loop-mobile-header-v2 .loop-mobile-logo-btn img {
          max-width: 92px !important;
        }

        #app-main-header.loop-mobile-header-v2 .loop-mobile-actions-v2 {
          gap: 4px !important;
          max-width: calc(100vw - 104px) !important;
        }

        #app-main-header.loop-mobile-header-v2 .loop-mobile-icon-btn-v2 {
          width: 36px !important;
          height: 36px !important;
          min-width: 36px !important;
          min-height: 36px !important;
          max-width: 36px !important;
          max-height: 36px !important;
          flex-basis: 36px !important;
        }
      }

      @media (max-width: 360px) {
        #app-main-header.loop-mobile-header-v2 .loop-mobile-logo-btn,
        #app-main-header.loop-mobile-header-v2 .loop-mobile-logo-btn img {
          max-width: 76px !important;
        }

        #app-main-header.loop-mobile-header-v2 .loop-mobile-actions-v2 {
          gap: 3px !important;
          max-width: calc(100vw - 88px) !important;
        }

        #app-main-header.loop-mobile-header-v2 .loop-mobile-icon-btn-v2 {
          width: 32px !important;
          height: 32px !important;
          min-width: 32px !important;
          min-height: 32px !important;
          max-width: 32px !important;
          max-height: 32px !important;
          flex-basis: 32px !important;
        }
      }


      /* =========================================================
         NATIVE APP SAFE-AREA LOCK — Android / Capacitor
         Web mobile already reserves browser chrome correctly, but the native
         WebView can run edge-to-edge and report env(safe-area-inset-top) as 0.
         App.js adds body.is-mobile-app when running inside Capacitor, so this
         fallback only affects the installed app and does not change web.
         ========================================================= */
      body.is-mobile-app {
        --loop-native-statusbar-fallback: 30px;
        --loop-safe-top: max(env(safe-area-inset-top, 0px), var(--loop-native-statusbar-fallback));
        --loop-safe-bottom: max(env(safe-area-inset-bottom, 0px), 34px);
        --loop-mobile-header-height: 58px;
        --loop-mobile-header-total: calc(var(--loop-safe-top) + var(--loop-mobile-header-height));
      }

      @media (max-width: 920px) {
        body.is-mobile-app #app-main-header.loop-mobile-header-v2,
        html body.is-mobile-app #root #app-main-header.loop-mobile-header-v2 {
          top: 0 !important;
          min-height: var(--loop-mobile-header-total) !important;
          height: var(--loop-mobile-header-total) !important;
          padding-top: var(--loop-safe-top) !important;
          padding-bottom: 0 !important;
          box-sizing: border-box !important;
          overflow: hidden !important;
        }

        body.is-mobile-app #app-main-header.loop-mobile-header-v2 .loop-mobile-header-row,
        html body.is-mobile-app #root #app-main-header.loop-mobile-header-v2 .loop-mobile-header-row {
          height: var(--loop-mobile-header-height) !important;
          min-height: var(--loop-mobile-header-height) !important;
          max-height: var(--loop-mobile-header-height) !important;
        }

        body.is-mobile-app .loop-mobile-search-sheet-v2 {
          padding-top: var(--loop-safe-top) !important;
        }
      }

    

      /* LOOP MOBILE HEADER GLOBAL STABILITY LOCK */
      #app-main-header.loop-mobile-header-v2,
      body #app-main-header.loop-mobile-header-v2,
      html body #root #app-main-header.loop-mobile-header-v2 {
        position: sticky !important;
        top: 0 !important;
        left: auto !important;
        right: auto !important;
        z-index: var(--loop-z-header, 1000) !important;
        width: 100% !important;
        height: calc(env(safe-area-inset-top, 0px) + 58px) !important;
        min-height: calc(env(safe-area-inset-top, 0px) + 58px) !important;
        max-height: calc(env(safe-area-inset-top, 0px) + 58px) !important;
        padding-top: env(safe-area-inset-top, 0px) !important;
        margin: 0 !important;
        display: block !important;
        flex: 0 0 auto !important;
        overflow: visible !important;
        transform: translateZ(0) !important;
        contain: layout style paint !important;
        isolation: isolate !important;
        background-color: #134f47 !important;
        background-image:
          radial-gradient(circle at 10% 0%, rgba(255,248,238,.12), transparent 28%),
          linear-gradient(135deg, #103f39 0%, #14554d 56%, #1d6b61 100%) !important;
        box-shadow: 0 10px 26px rgba(16,63,57,.18) !important;
      }

      #app-main-header.loop-mobile-header-v2 .loop-mobile-header-row {
        height: 58px !important;
        min-height: 58px !important;
        max-height: 58px !important;
        padding: 8px 10px !important;
        display: grid !important;
        grid-template-columns: minmax(84px, auto) minmax(0, 1fr) auto !important;
        align-items: center !important;
        gap: 8px !important;
        overflow: visible !important;
      }

      #app-main-header.loop-mobile-header-v2 .loop-mobile-logo-btn {
        height: 42px !important;
        max-height: 42px !important;
        display: inline-flex !important;
        align-items: center !important;
        justify-content: flex-start !important;
        overflow: visible !important;
      }

      #app-main-header.loop-mobile-header-v2 .loop-mobile-logo-btn img {
        max-height: 38px !important;
        max-width: 112px !important;
        object-fit: contain !important;
      }

      #app-main-header.loop-mobile-header-v2 .loop-mobile-actions-v2 {
        height: 42px !important;
        max-height: 42px !important;
        display: flex !important;
        align-items: center !important;
        justify-content: flex-end !important;
        gap: 6px !important;
        overflow: visible !important;
      }

      #app-main-header.loop-mobile-header-v2 .loop-mobile-icon-btn-v2 {
        width: 40px !important;
        height: 40px !important;
        min-width: 40px !important;
        min-height: 40px !important;
        max-width: 40px !important;
        max-height: 40px !important;
        flex: 0 0 40px !important;
        margin: 0 !important;
      }

      #app-main-header.loop-mobile-header-v2 + .loop-mobile-spacer-v2 {
        display: none !important;
        height: 0 !important;
        min-height: 0 !important;
        max-height: 0 !important;
      }

      @media (max-width: 380px) {
        #app-main-header.loop-mobile-header-v2 .loop-mobile-header-row {
          padding-left: 8px !important;
          padding-right: 8px !important;
          gap: 5px !important;
        }

        #app-main-header.loop-mobile-header-v2 .loop-mobile-logo-btn img {
          max-width: 96px !important;
        }

        #app-main-header.loop-mobile-header-v2 .loop-mobile-actions-v2 {
          gap: 5px !important;
        }

        #app-main-header.loop-mobile-header-v2 .loop-mobile-icon-btn-v2 {
          width: 38px !important;
          height: 38px !important;
          min-width: 38px !important;
          min-height: 38px !important;
          max-width: 38px !important;
          max-height: 38px !important;
          flex-basis: 38px !important;
        }
      }



      /* LOOP MOBILE HEADER GLOBAL FIXED LOCK */
      :root {
        --loop-global-header-mobile-h: calc(env(safe-area-inset-top, 0px) + 58px);
      }

      body {
        padding-top: var(--loop-global-header-mobile-h) !important;
      }

      #app-main-header.loop-mobile-header-v2,
      body #app-main-header.loop-mobile-header-v2,
      html body #root #app-main-header.loop-mobile-header-v2 {
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        right: 0 !important;
        bottom: auto !important;
        z-index: var(--loop-z-header, 1000) !important;
        width: 100% !important;
        height: var(--loop-global-header-mobile-h) !important;
        min-height: var(--loop-global-header-mobile-h) !important;
        max-height: var(--loop-global-header-mobile-h) !important;
        margin: 0 !important;
        padding-top: env(safe-area-inset-top, 0px) !important;
        display: block !important;
        flex: 0 0 auto !important;
        overflow: visible !important;
        transform: translateZ(0) !important;
        will-change: transform !important;
        contain: layout style paint !important;
        isolation: isolate !important;
      }

      #app-main-header.loop-mobile-header-v2 + .loop-mobile-spacer-v2 {
        display: none !important;
        height: 0 !important;
        min-height: 0 !important;
        max-height: 0 !important;
      }



/* =========================================================
   LOOP NATIVE CONTENT CLEARANCE V26
   Diagnosis from real Android WebView:
   - V25 is present and active.
   - Header bottom = 94px.
   - body padding-top = 94px.
   - root starts at y=94px.
   So the native safe-area/header offset is working.

   Remaining issue:
   Some route internals start exactly at the header edge or use tiny negative
   margins, so visually they still feel "eaten" by the fixed header.
   V26 adds a small native content clearance for normal routes, while keeping
   full-screen calendar routes tight and controlled.
   ========================================================= */

html.is-mobile-app,
body.is-mobile-app {
  --loop-native-statusbar-fallback: 30px !important;
  --loop-safe-top: max(env(safe-area-inset-top, 0px), var(--loop-native-statusbar-fallback)) !important;
  --loop-mobile-header-row-h: 64px !important;
  --loop-mobile-header-total: calc(var(--loop-safe-top) + var(--loop-mobile-header-row-h)) !important;
  --loop-native-header-h: var(--loop-mobile-header-total) !important;
  --loop-native-content-gap: 14px !important;
}

/* Keep the V25 model: body reserves the fixed header. */
body.is-mobile-app {
  padding-top: var(--loop-mobile-header-total) !important;
  margin-top: 0 !important;
}

body.is-mobile-app #app-main-header,
body.is-mobile-app header#app-main-header,
body.is-mobile-app #app-main-header.loop-mobile-header-v2,
body.is-mobile-app #app-main-header.loop-ref-header {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  height: var(--loop-mobile-header-total) !important;
  min-height: var(--loop-mobile-header-total) !important;
  max-height: var(--loop-mobile-header-total) !important;
  padding-top: var(--loop-safe-top) !important;
  margin: 0 !important;
  z-index: var(--loop-z-header, 1000) !important;
  transform: none !important;
}

/* Header-only mount point should not reserve extra height.
   Body already reserves the header. */
body.is-mobile-app .mainmenu-header-only-root {
  height: 0 !important;
  min-height: 0 !important;
  max-height: 0 !important;
  flex: 0 0 0 !important;
  padding: 0 !important;
  margin: 0 !important;
  overflow: visible !important;
  background: transparent !important;
}

/* Home: V25 made the math correct, but the hero had a -2px visual start.
   Give native home a small breathing gap under the fixed header. */
body.is-mobile-app .mainmenu-root.has-full-content > main.loop-pro-main,
body.is-mobile-app .mainmenu-root.has-full-content .loop-pro-main {
  padding-top: var(--loop-native-content-gap) !important;
  margin-top: 0 !important;
}

body.is-mobile-app .mainmenu-root.has-full-content .loop-p6-hero {
  margin-top: 0 !important;
}

/* Normal headerOnly routes: add a small top clearance, but do not affect
   full-screen calendar or public business pages. */
body.is-mobile-app .mainmenu-header-only-root + :not(.calendar-page-wrap):not(.business-page):not(.business-public-page),
body.is-mobile-app .mainmenu-header-only-root ~ :not(.calendar-page-wrap):not(.business-page):not(.business-public-page) {
  margin-top: var(--loop-native-content-gap) !important;
}

/* Common app pages that sometimes use internal negative margins. */
body.is-mobile-app .owner-businesses-page,
body.is-mobile-app .owner-businesses-shell,
body.is-mobile-app .ob-page,
body.is-mobile-app .my-bookings-page,
body.is-mobile-app .bookings-page,
body.is-mobile-app .my-reservations-page,
body.is-mobile-app .business-edit-page,
body.is-mobile-app .business-edit-shell {
  padding-top: var(--loop-native-content-gap) !important;
  box-sizing: border-box !important;
}

/* Avoid double-gap if a page is also the direct sibling of headerOnly. */
body.is-mobile-app .mainmenu-header-only-root + .owner-businesses-page,
body.is-mobile-app .mainmenu-header-only-root + .owner-businesses-shell,
body.is-mobile-app .mainmenu-header-only-root + .ob-page,
body.is-mobile-app .mainmenu-header-only-root + .my-bookings-page,
body.is-mobile-app .mainmenu-header-only-root + .bookings-page,
body.is-mobile-app .mainmenu-header-only-root + .my-reservations-page,
body.is-mobile-app .mainmenu-header-only-root + .business-edit-page,
body.is-mobile-app .mainmenu-header-only-root + .business-edit-shell {
  margin-top: 0 !important;
}

/* Public business page was already correct. Keep it without extra clearance. */
body.is-mobile-app .business-page,
body.is-mobile-app .business-public-page {
  margin-top: 0 !important;
}

/* Full-screen calendar: do not add the normal route gap. */
body.is-mobile-app .mainmenu-header-only-root + .calendar-page-wrap,
body.is-mobile-app .mainmenu-header-only-root ~ .calendar-page-wrap,
body.is-mobile-app .calendar-page-wrap:has(.admin-calendar-root.phase23-clean),
body.is-mobile-app .calendar-page-wrap {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

`;

    const openMobileSearch = () => {
      setShowMobileSearch(true);
      if (typeof setShowSuggestions === 'function') {
        setShowSuggestions(Boolean(effectiveQ && String(effectiveQ).trim()));
      }
    };

    return (
      <>
        <style>{mobileHeaderStyles}</style>

        <header
          id="app-main-header"
          className="loop-mobile-header-v2 loop-header-emerald-hardlock"
          aria-label={t("Navegación principal Loop")}
          style={{
            backgroundColor: LOOP_HEADER_LOCK_BG_COLOR,
            backgroundImage: LOOP_HEADER_LOCK_BG_IMAGE,
            background: LOOP_HEADER_LOCK_BG,
            borderBottom: '0',
            boxShadow: '0 12px 30px rgba(16,63,57,.20)',
          }}
        >
          <div className="loop-mobile-header-row">
            <button type="button" className="loop-mobile-logo-btn" onClick={handleLogoClick} aria-label={t("Inicio")}>
              <LoopLogo alt="Loop" native height={40} width={132} />
            </button>

            <div className="loop-mobile-actions-v2"><MarketPicker />








              {hasUser ? (
                <NotificationCenter
                  db={db}
                  userEmail={userEmail}
                  fallbackUnreadCount={pendingCount}
                  activeBusinessId={notificationBusinessContext.businessId}
                  activeBusinessName={notificationBusinessContext.businessName}
                  buttonClassName="loop-mobile-icon-btn-v2"
                  badgeClassName="loop-mobile-badge-v2"
                  icon={<BellIconSvg />}
                  mobile
                />
              ) : null}

              <button
                ref={avatarTriggerRef}
                type="button"
                className="loop-mobile-icon-btn-v2 primary"
                onClick={() => {
                  if (!hasUser) { handleLoginClick(); return; }
                  if (typeof setOpenAvatar === 'function') setOpenAvatar(true);
                }}
                aria-label={hasUser ? "Perfil" : t("Conectarse")}
              >
                {hasUser ? <span style={{ fontWeight: 950, fontSize: 14 }}>{String(userInitial).toUpperCase()}</span> : <UserIconSvg />}
              </button>
            </div>
          </div>
        </header>

        {hasUser ? <MobileNavigation>
          <button type="button" aria-current={currentPath === "/" ? "page" : undefined} onClick={handleLogoClick}><PinIconSvg /><span>{t("Explorar")}</span></button>
          {ownsBusinessForUi ? <button type="button" aria-current={currentPath.includes("owner-businesses") ? "page" : undefined} onClick={() => typeof onAdminManage === 'function' ? onAdminManage() : navigate('/owner-businesses')}><BusinessIconSvg /><span>{t("Mis negocios")}</span></button> : null}
          {canUseCalendarForUi ? <button type="button" aria-current={currentPath.includes("calendar") ? "page" : undefined} onClick={handleCalendarClick}><CalendarIconSvg /><span>Agenda</span></button> : null}
          <button type="button" aria-current={currentPath.includes("my-bookings") ? "page" : undefined} onClick={handleMyBookingsClick}><BookmarkIconSvg /><span>{t("Mis reservas")}</span></button>
        </MobileNavigation> : null}

        {hasUser ? (
          <div className="loop-mobile-avatar-dropdown-mount" aria-hidden={!openAvatar ? "true" : undefined}>
            <AvatarDropdown
              userName={userName}
              userEmail={userEmail}
              open={openAvatar}
              setOpen={setOpenAvatar}
              hideTrigger={true}
              externalTriggerRef={avatarTriggerRef}
              onShowUserProfile={onShowUserProfile}
              onAdminManage={onAdminManage}
              onShowMyBookings={onShowMyBookings}
              onShowMyLoyalty={handleMyLoyaltyClick}
              onShowPending={onShowPending}
              onCreateBusiness={onCreateBusiness}
              onLogout={onLogout}
              showBusinessAdmin={ownsBusinessForUi}
              onPlatformAdmin={isAdminApprover ? () => navigate("/owner-console") : null}
            />
          </div>
        ) : null}

        <div aria-hidden="true" className="loop-mobile-spacer-v2" style={{ height: spacerHeight }} />

        {showLoginSheet && !hasUser ? (
          <div className="loop-mobile-login-sheet-v2" role="dialog" aria-modal="true" aria-label={t("Conectarse a Loop")}>
            <button
              type="button"
              aria-label={t("Cerrar conectarse")}
              onClick={() => setShowLoginSheet(false)}
              style={{ position: 'absolute', inset: 0, border: 0, background: 'rgba(15,23,42,0.36)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', padding: 0, margin: 0 }}
            />
            <div className="loop-mobile-login-panel-v2">
              <div style={{ padding: '10px 0 6px', display: 'flex', justifyContent: 'center' }}>
                <div aria-hidden="true" style={{ width: 42, height: 5, borderRadius: 999, background: '#cbd5e1' }} />
              </div>
              <div style={{ padding: '14px 18px 18px' }}>
                <h3 style={{ margin: 0, color: '#0f172a', fontSize: 26, lineHeight: 1.05, fontWeight: 950, letterSpacing: '-0.05em' }}>{t("Conéctate a Loop")}</h3>
                <p style={{ margin: '9px 0 18px', color: '#64748b', fontSize: 14, lineHeight: 1.45, fontWeight: 700 }}>{t("Guarda tus favoritos, administra tus reservas y accede a calendarios de negocios o trabajadores.")}</p>
                <button type="button" onClick={() => runLoginAction('/login')} style={{ width: '100%', minHeight: 52, borderRadius: 18, border: 0, background: 'linear-gradient(135deg, #00675f, #00897f)', color: '#ffffff', fontWeight: 950, fontSize: 15 }}>{t("Iniciar sesión")}</button>
                <button type="button" onClick={() => runLoginAction('/register')} style={{ width: '100%', minHeight: 50, marginTop: 10, borderRadius: 18, border: '1px solid #e2e8f0', background: '#ffffff', color: '#0f172a', fontWeight: 900, fontSize: 14 }}>{t("Crear cuenta")}</button>
                <button type="button" onClick={() => setShowLoginSheet(false)} style={{ width: '100%', minHeight: 44, marginTop: 8, borderRadius: 16, border: 0, background: 'transparent', color: '#64748b', fontWeight: 800, fontSize: 13 }}>{t("Seguir explorando")}</button>
              </div>
            </div>
          </div>
        ) : null}

        {showMobileSearch ? (
          <div
            ref={mobileSearchRef}
            className="loop-mobile-search-sheet-v2"
            onMouseDown={(e) => { if (e.target === e.currentTarget) closeSearchUI({ closeMobile: true }); }}
          >
            <form onSubmit={handleSubmit} className="loop-mobile-search-top-v2">
              <div className="loop-mobile-search-field-v2">
                <span><FaSearch size={17} /></span>
                <input
                  value={effectiveQ}
                  onChange={(e) => {
                    setEffectiveQ(e.target.value);
                    if (typeof setShowSuggestions === 'function') {
                      setShowSuggestions(true);
                    }
                  }}
                  onFocus={() => {
                    if (typeof setShowSuggestions === 'function') setShowSuggestions(true);
                  }}
                  placeholder={t("Busca un servicio o negocio")}
                  autoFocus
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  name="loop-mobile-search"
                  inputMode="search"
                  aria-label={t("Busca un servicio o negocio")}
                />
              </div>
              <button type="button" className="loop-mobile-search-close-v2" onClick={() => closeSearchUI({ clearQuery: true, closeMobile: true })} aria-label={t("Cerrar búsqueda")}>
                <FaTimes size={16} />
              </button>
            </form>

            <div className="loop-mobile-search-results-v2">
              {showSuggestions ? (
                <SearchSuggestions
                  onlyPublicBusinesses={true}
                  q={effectiveQ}
                  db={db}
                  locations={locationSuggestions}
                  locationLoading={locationLoading}
                  onSelectBusiness={handleSelectBusinessFromSuggestions}
                  onSelectService={handleSelectServiceFromSuggestions}
                  onSelectLocation={handleSelectLocationFromSuggestions}
                />
              ) : null}
            </div>
          </div>
        ) : null}
      </>
    );
  }

  return (
    <header id="app-main-header" className="w-full" style={{ position: 'sticky', top: 0, zIndex: 60, paddingTop: 'env(safe-area-inset-top)', backgroundColor: PRIMARY_COLOR, borderBottom: '0', boxShadow: HEADER_SHADOW }}>
      <div className="w-full">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', paddingTop: 8, paddingBottom: 4 }}>
            <button type="button" onClick={handleLogoClick} style={{ background: 'transparent', border: 'none', padding: 0, display: 'flex', alignItems: 'center' }}>
              <LoopLogo alt="Loop" height={displayLogoHeight} width={logoMaxWidth} />
            </button>
          </div>

          <MarketPicker />
        </div>

        <div style={{ position: 'relative', zIndex: 200 }}>
          {hasUser && (
            <AvatarDropdown
              userName={userName}
              userEmail={userEmail}
              open={openAvatar}
              setOpen={setOpenAvatar}
              hideTrigger={true}
              externalTriggerRef={avatarTriggerRef}
              onShowUserProfile={onShowUserProfile}
              onAdminManage={onAdminManage}
              onShowMyBookings={onShowMyBookings}
              onShowMyLoyalty={handleMyLoyaltyClick}
              onShowPending={onShowPending}
              onCreateBusiness={onCreateBusiness}
              onLogout={onLogout}
              showBusinessAdmin={ownsBusinessForUi}
              onPlatformAdmin={isAdminApprover ? () => navigate("/owner-console") : null}
            />
          )}
        </div>

        {showMobileSearch && (
          <div
            ref={mobileSearchRef}
            onMouseDown={(e) => { if (e.target === e.currentTarget) closeSearchUI({ closeMobile: true }); }}
            className="fixed inset-0 z-50 bg-white/95 flex flex-col items-center justify-start loop-native-search-overlay"
            style={{
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              paddingTop: 'env(safe-area-inset-top, 24px)',
            }}
          >
            <form onSubmit={handleSubmit} className="flex w-full max-w-xl mx-auto mt-4 items-center px-4">
              <input
                value={effectiveQ}
                onChange={(e) => {
                  setEffectiveQ(e.target.value);
                  if (typeof setShowSuggestions === 'function') {
                    setShowSuggestions(Boolean(e.target.value && String(e.target.value).trim()));
                  }
                }}
                placeholder={t("Busca un servicio o negocio")}
                className="flex-1 py-2 px-4 rounded-full border"
                style={{ minHeight: 44, outline: 'none' }}
                autoFocus
                aria-label={t("Busca un servicio o negocio")}
              />
              <button
                type="button"
                onClick={() => closeSearchUI({ clearQuery: true, closeMobile: true })}
                style={{ marginLeft: 12, fontSize: 22, background: 'transparent', border: 'none', color: '#666' }}
              >
                <FaTimes />
              </button>
            </form>

            {showSuggestions && (
              <div style={{ width: '100%', maxWidth: 600, marginTop: 8, zIndex: 90 }}>
                <SearchSuggestions
                  onlyPublicBusinesses={true}
                  q={effectiveQ}
                  db={db}
                  locations={locationSuggestions}
                  locationLoading={locationLoading}
                  onSelectBusiness={handleSelectBusinessFromSuggestions}
                  onSelectService={handleSelectServiceFromSuggestions}
                  onSelectLocation={handleSelectLocationFromSuggestions}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}

Header.propTypes = {
  db: PropTypes.any,
  logoWidth: PropTypes.number,
  logoHeight: PropTypes.number,
  q: PropTypes.string,
  setQ: PropTypes.func,
  onSearch: PropTypes.func,
  showSuggestions: PropTypes.bool,
  setShowSuggestions: PropTypes.func,
  openAvatar: PropTypes.bool,
  setOpenAvatar: PropTypes.func,
  pendingCount: PropTypes.number,
  calendarPendingCount: PropTypes.number,
  onHomeClick: PropTypes.func,
  onOpenBusinessSelector: PropTypes.func,
  onShowUserProfile: PropTypes.func,
  onAdminManage: PropTypes.func,
  onShowMyBookings: PropTypes.func,
  onShowMyLoyalty: PropTypes.func,
  onShowPending: PropTypes.func,
  onCreateBusiness: PropTypes.func,
  onLogout: PropTypes.func,
  onLogin: PropTypes.func,
  onOpenCalendar: PropTypes.func,
  onSearchNearby: PropTypes.func,
  userName: PropTypes.string,
  userEmail: PropTypes.string,
  isLoggedIn: PropTypes.bool,
  authReady: PropTypes.bool,
  forceNativeLayout: PropTypes.bool,
  businessAccessOverride: PropTypes.bool,
  ownsBusinessOverride: PropTypes.bool,
};

export { Header };
export default Header;
